import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { Modal } from '@/components/ui/Modal';
import { ManualEntryImagesField } from '@/components/ManualEntryImagesField';
import { inputClass, labelClass, inlineErrorClass, inlineSuccessClass } from '@/components/ui/inputStyles';
import { useSession } from '@/hooks/useSession';
import { draftWechatSummary } from '@/lib/draftStatusUi';
import { formatEntryRelocateMessage, type EntryRelocateResponse } from '@/lib/entryRelocateMessage';
import { buildCatalogSiteOptionVMs, type CatalogSiteOptionVM } from '@/lib/catalogSiteTree';
import { SiteHierarchyCheckboxList } from '@/components/SiteHierarchyCheckboxList';
import { SiteWecomGroupsEditor } from '@/components/SiteWecomGroupsEditor';
import { MiniprogramPublishModeCard } from '@/components/MiniprogramPublishModeCard';
import { MiniprogramReviewSettingsCard } from '@/components/MiniprogramReviewSettingsCard';
import { MiniprogramWecomPushCard } from '@/components/MiniprogramWecomPushCard';
import { MiniprogramFeaturesCard } from '@/components/MiniprogramFeaturesCard';
import {
  normalizeMpLegalDocs,
  normalizeMpUserSelfSettings,
  normalizeMpUserSelfWecomPush,
  normalizePublishMode,
  type MiniproPublishMode,
  type MpLegalDocs,
  type MpUserSelfSettings,
} from '@/lib/mpUserSelfPublish';
import { MiniprogramAdsPanel } from '@/components/MiniprogramAdsPanel';
import { SiteSubscribePublishHierarchyEditor } from '@/components/SiteSubscribePublishHierarchyEditor';
import { SiteFloatActionsEditor } from '@/components/SiteFloatActionsEditor';
import type { SiteFloatActionsOverride } from '@/components/SiteFloatActionsEditor';
import { SiteMiniproFeaturesEditor } from '@/components/SiteMiniproFeaturesEditor';
import type { SiteWecomGroupRow } from '@/lib/miniproWecomSite';
import { defaultMiniproFeatures, normalizeMiniproFeatures, type MiniproFeatures } from '@/lib/miniproFeatures';
import { defaultDialLimit, normalizeDialLimit, type DialLimitConfig, DIAL_LIMIT_DEFAULT_EXHAUSTED_BODY, DIAL_LIMIT_DEFAULT_EXHAUSTED_TITLE } from '@/lib/dialLimit';
import {
  defaultAdReward,
  normalizeAdReward,
  type AdRewardConfig,
  type PinAcquireMethod,
} from '@/lib/adReward';
import { MiniprogramAdRewardSettingsCard } from '@/components/MiniprogramAdRewardSettingsCard';

type SiteItem = {
  id: number;
  site_name: string;
  site_code: string;
  enabled?: boolean;
  /** 一级站点为 null/0；二级为所属一级 id */
  parent_site_id?: number | null;
};

type SlideRow = { image_url: string; title?: string; link_url?: string };
type TypeRow = {
  key: string;
  label: string;
  default_cover_path?: string;
  /** 该类型发布是否必进审核 */
  require_review?: boolean;
  /** 小程序发布页/提交成功提示（可选） */
  review_hint?: string;
  /** 该类型专属客服二维码（留空则用全站发布导流二维码） */
  review_service_qr_path?: string;
  /** 是否覆盖小程序总置顶方式 */
  pin_override?: boolean;
  pin_method?: PinAcquireMethod;
  pin_owner_enabled?: boolean;
  pin_other_enabled?: boolean;
  pin_reward_days?: number;
  pin_owner_daily_limit?: number;
  pin_other_daily_limit?: number;
  share_open_required?: number;
  share_pin_days?: number;
  share_require_new_user?: boolean;
  service_qr_path?: string;
  service_title?: string;
  service_body?: string;
};
/** 单站点对订阅/发布导流页的覆盖（字段留空则沿用全站默认） */
type SiteSubscribePublishOverride = {
  subscribe_mp_qr_path?: string;
  subscribe_title?: string;
  subscribe_body?: string;
  publish_service_qr_path?: string;
  publish_title?: string;
  publish_body?: string;
};

type Miniprogram = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
  nav_title: string;
  share_title: string;
  /** 微信小程序 AppID（wx 开头 18 位），用于公众号草稿正文中的小程序文字链 */
  wechat_miniprogram_appid?: string | null;
  /** 微信小程序 AppSecret（用于生成 #小程序:// 短链） */
  wechat_miniprogram_appsecret?: string | null;
  share_image_path: string | null;
  list_increment_views_enabled: boolean;
  /** 列表/详情站点名：二级站是否显示为「一级.二级」 */
  list_site_name_with_parent_enabled?: boolean;
  share_actions_enabled: boolean;
  /** 详情页转发卡片配图：cover=配图，title=标题图，cover_title=封面+标题 */
  detail_share_image_mode?: 'cover' | 'title' | 'cover_title';
  list_page_size: number;
  hot_search_max_len?: number;
  hot_search_allow_symbols?: boolean;
  slides: SlideRow[];
  site_ids: number[];
  types: TypeRow[];
  site_wecom_groups: Record<string, SiteWecomGroupRow>;
  auto_heat: {
    enabled: boolean;
    dedupe_window_seconds: number;
    heat_probability_percent: number;
    heat_min_increment: number;
    heat_max_increment: number;
    heat_daily_cap: number;
  };
  detail_notice: {
    enabled: boolean;
    title: string;
    items: string[];
    more_tip: string;
  };
  /** 底部导航：首页（默认开） */
  footer_tab_home_enabled?: boolean;
  footer_tab_subscribe_enabled?: boolean;
  footer_tab_publish_enabled?: boolean;
  /** 发布页连续点击 pub-hero 打开站长 H5（/w/login） */
  site_master_portal_enabled?: boolean;
  site_master_portal_tap_count?: number;
  subscribe_mp_qr_path?: string | null;
  subscribe_title?: string;
  subscribe_body?: string;
  publish_service_qr_path?: string | null;
  publish_title?: string;
  publish_body?: string;
  /** 置顶信息在列表中的相对顺序：剩余天数优先 或 刷新时间优先 */
  catalog_sticky_sort_mode?: 'remaining_days' | 'refresh_time';
  /** 按站点 ID（字符串键）覆盖订阅/发布页 */
  site_subscribe_publish_pages?: Record<string, SiteSubscribePublishOverride>;
  /** 按站点覆盖悬浮钮二维码 */
  site_float_actions_pages?: Record<string, SiteFloatActionsOverride>;
  /** 详情页「拨打电话」下方提示文案模板；占位符 {miniprogram_name}（本记录名称）、{site_name}（本条站点名）；留空用默认 */
  phone_contact_hint_template?: string;
  /** 拨号前温馨提醒弹窗 */
  phone_dial_reminder?: {
    enabled: boolean;
    title: string;
    body: string;
    confirm_text: string;
  };
  /** 拨号次数限制（每日配额 + 激励广告赠送） */
  dial_limit?: DialLimitConfig;
  ad_reward?: AdRewardConfig;
  features?: MiniproFeatures;
  site_minipro_features_pages?: Record<string, MiniproFeatures>;
  /**
   * 信息管理：可筛选/可迁入的站点（展示站点勾选 id 经服务端展开后的启用站点，含二级站）。
   * 与 list_miniprogram_catalog、move_entry_site 口径一致。
   */
  entry_site_options?: Array<{ id: number; site_name: string }>;
  /** 从属主小程序 id；空为独立/主小程序 */
  master_miniprogram_id?: number | null;
  master_name?: string;
  is_slave?: boolean;
  /** 主小程序已选站点（含子站展开），从属小程序勾选展示站点时不可超出此集合 */
  master_site_ids_expanded?: number[];
  publish_mode?: MiniproPublishMode;
  mp_legal_docs?: MpLegalDocs;
  mp_user_self_settings?: MpUserSelfSettings;
};

type CatalogRow = {
  id: number;
  site_id: number;
  site_name: string;
  title_text: string | null;
  body_text: string | null;
  type_key: string;
  draft_status?: string | null;
  wechat_draft_media_id?: string | null;
  is_sticky?: boolean;
  sticky_expired?: boolean;
  sticky_remaining_days?: number;
  pin_status_text?: string;
  published_at?: string | null;
  refreshed_at?: string | null;
  refreshed_at_display?: string;
  effective_refreshed_at?: string | null;
  minipro_pinned_until?: string | null;
  minipro_refreshed_at?: string | null;
  created_at?: string;
  sold_out?: boolean;
};

const CAT_PER = 15;
const HEAT_PRESETS = {
  conservative: {
    dedupe_window_seconds: 21600,
    heat_probability_percent: 20,
    heat_min_increment: 1,
    heat_max_increment: 2,
    heat_daily_cap: 120,
  },
  standard: {
    dedupe_window_seconds: 14400,
    heat_probability_percent: 35,
    heat_min_increment: 1,
    heat_max_increment: 3,
    heat_daily_cap: 200,
  },
  aggressive: {
    dedupe_window_seconds: 7200,
    heat_probability_percent: 55,
    heat_min_increment: 2,
    heat_max_increment: 5,
    heat_daily_cap: 500,
  },
} as const;
const DETAIL_NOTICE_PRESETS = {
  resale: {
    title: '交易安全',
    items: [
      '仅支持当面验货、现场成交；勿向陌生人转账、勿汇款。',
      '勿扫不明收款码；勿脱离当面场景私下付款。',
    ],
    more_tip: '不要转账不要汇款！线下交易，验货面付！',
  },
  hiring: {
    title: '招聘防骗提示',
    items: [
      '求职前请核实企业信息与岗位真实性，警惕“高薪低门槛”陷阱。',
      '正规招聘不会以体检费、押金、培训费等名义要求先付款。',
      '面试建议选择公开场所并告知家人行程，保护个人证件与隐私信息。',
    ],
    more_tip: '以下推荐岗位请注意甄别资质与合同条款，谨防虚假招聘信息',
  },
  marriage: {
    title: '交友征婚提醒',
    items: [
      '初次接触请先核实基础身份信息，谨慎提供住址、银行卡等敏感信息。',
      '涉及金钱往来请务必提高警惕，避免因“借款、投资、代付”造成损失。',
      '线下见面建议选择公共场所并告知亲友行程，确保人身安全。',
    ],
    more_tip: '以下推荐信息仅作参考，请在充分沟通与核实后再做决定',
  },
} as const;

/** 生成符合服务端 `^[a-z0-9_]{1,48}$` 的类型键，并写入 `usedKeys`（避免重复）。 */
function generateMiniproTypeKey(usedKeys: Set<string>): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  for (let attempt = 0; attempt < 64; attempt++) {
    const u = new Uint8Array(10);
    crypto.getRandomValues(u);
    let suffix = '';
    for (let i = 0; i < 10; i++) suffix += chars[u[i]! % chars.length];
    const k = `t_${suffix}`;
    if (!usedKeys.has(k)) {
      usedKeys.add(k);
      return k;
    }
  }
  const k = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  usedKeys.add(k);
  return k;
}

function ensureMiniproTypeKeys(types: TypeRow[]): TypeRow[] {
  const used = new Set<string>();
  return types.map((t) => {
    const raw = (t.key || '').trim();
    const dc = (t.default_cover_path || '').trim();
    const row: TypeRow = { key: '', label: t.label };
    if (dc) row.default_cover_path = dc;
    if (t.require_review) row.require_review = true;
    const rh = (t.review_hint || '').trim();
    if (rh) row.review_hint = rh;
    const rq = (t.review_service_qr_path || '').trim();
    if (rq) row.review_service_qr_path = rq;
    if (t.pin_override && (t.pin_method === 'ad' || t.pin_method === 'share' || t.pin_method === 'service')) {
      row.pin_override = true;
      row.pin_method = t.pin_method;
      if (t.pin_method === 'ad') {
        if (t.pin_owner_enabled) row.pin_owner_enabled = true;
        if (t.pin_other_enabled) row.pin_other_enabled = true;
        if (typeof t.pin_reward_days === 'number') row.pin_reward_days = t.pin_reward_days;
        if (typeof t.pin_owner_daily_limit === 'number') row.pin_owner_daily_limit = t.pin_owner_daily_limit;
        if (typeof t.pin_other_daily_limit === 'number') row.pin_other_daily_limit = t.pin_other_daily_limit;
      } else if (t.pin_method === 'share') {
        if (typeof t.share_open_required === 'number') row.share_open_required = t.share_open_required;
        if (typeof t.share_pin_days === 'number') row.share_pin_days = t.share_pin_days;
        if (t.share_require_new_user) row.share_require_new_user = true;
      } else if (t.pin_method === 'service') {
        const sqr = (t.service_qr_path || '').trim();
        if (sqr) row.service_qr_path = sqr;
        const st = (t.service_title || '').trim();
        if (st) row.service_title = st;
        const sb = (t.service_body || '').trim();
        if (sb) row.service_body = sb;
      }
    }
    if (/^[a-z0-9_]{1,48}$/.test(raw) && !used.has(raw)) {
      used.add(raw);
      row.key = raw;
      return row;
    }
    row.key = generateMiniproTypeKey(used);
    return row;
  });
}

function shortTypeLabel(label: string): string {
  const text = (label || '').trim();
  if (text.length <= 4) return text || '（未命名）';
  return `${text.slice(0, 4)}…`;
}

/** 解析后台常见的 `YYYY-MM-DD` / `YYYY-MM-DD HH:mm:ss` / ISO 前缀，用于信息管理列表 */
function parseCatalogDateParts(raw: string): { yy: string; MM: string; dd: string; hh: string; mm: string } | null {
  const t = String(raw || '').trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?)?/);
  if (!m) return null;
  return {
    yy: m[1].slice(2),
    MM: m[2],
    dd: m[3],
    hh: m[4] ?? '00',
    mm: m[5] ?? '00',
  };
}

function fmtCatalogPublishedAt(s: string | null | undefined) {
  const p = parseCatalogDateParts(String(s || '').trim());
  if (!p) return '—';
  return `${p.yy}/${p.MM}/${p.dd} ${p.hh}:${p.mm}`;
}

function fmtCatalogRefreshedAt(s: string | null | undefined) {
  const t = String(s || '').trim();
  if (!t) return '-';
  if (/^0{4}-/.test(t)) return '-';
  const p = parseCatalogDateParts(t);
  if (!p) return '-';
  return `${p.yy}/${p.MM}/${p.dd} ${p.hh}:${p.mm}`;
}

async function uploadMiniproImage(miniprogramId: number, file: File): Promise<string> {
  const fd = new FormData();
  fd.append('miniprogram_id', String(miniprogramId));
  fd.append('file', file);
  const res = await fetch(apiUrl('upload_miniprogram_image'), { method: 'POST', body: fd, credentials: 'include' });
  const text = (await res.text()).replace(/^\uFEFF/, '').trim();
  const j = JSON.parse(text) as { ok?: boolean; local_path?: string; err?: string };
  if (!res.ok || !j.ok || !j.local_path) {
    throw new Error(j.err || `上传失败 HTTP ${res.status}`);
  }
  return j.local_path;
}

export function MiniprogramEditPage() {
  const { id: idParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const id = Number(idParam || 0);
  const { capabilities } = useSession();
  const isSysAdmin = capabilities.manage_users;

  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [mp, setMp] = useState<Miniprogram | null>(null);
  const [sites, setSites] = useState<SiteItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [heatPreset, setHeatPreset] = useState<'conservative' | 'standard' | 'aggressive'>('standard');
  const [detailNoticePreset, setDetailNoticePreset] = useState<'resale' | 'hiring' | 'marriage'>('resale');

  const [catPage, setCatPage] = useState(1);
  const [catSite, setCatSite] = useState(0);
  const [catQ, setCatQ] = useState('');
  const [catAppliedQ, setCatAppliedQ] = useState('');
  const [catType, setCatType] = useState('');
  const [catAppliedType, setCatAppliedType] = useState('');
  const [catItems, setCatItems] = useState<CatalogRow[]>([]);
  const viewRaw = searchParams.get('view');
  const pageView =
    viewRaw === 'content' || viewRaw === 'types' || viewRaw === 'ads' ? viewRaw : 'config';
  const switchView = useCallback(
    (next: 'config' | 'types' | 'content' | 'ads') => {
      const sp = new URLSearchParams(searchParams);
      sp.set('view', next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const siteSubPubPatch = useCallback((siteId: number, patch: Partial<SiteSubscribePublishOverride>) => {
    setMp((prev) => {
      if (!prev) return prev;
      const k = String(siteId);
      const pages = { ...(prev.site_subscribe_publish_pages || {}) };
      pages[k] = { ...(pages[k] || {}), ...patch };
      return { ...prev, site_subscribe_publish_pages: pages };
    });
  }, []);

  const siteSubPubClear = useCallback((siteId: number) => {
    setMp((prev) => {
      if (!prev) return prev;
      const k = String(siteId);
      const pages = { ...(prev.site_subscribe_publish_pages || {}) };
      delete pages[k];
      return { ...prev, site_subscribe_publish_pages: pages };
    });
  }, []);

  const siteFloatPatch = useCallback((siteId: number, patch: Partial<SiteFloatActionsOverride>) => {
    setMp((prev) => {
      if (!prev) return prev;
      const k = String(siteId);
      const pages = { ...(prev.site_float_actions_pages || {}) };
      pages[k] = { ...(pages[k] || {}), ...patch };
      return { ...prev, site_float_actions_pages: pages };
    });
  }, []);

  const siteFloatClear = useCallback((siteId: number) => {
    setMp((prev) => {
      if (!prev) return prev;
      const k = String(siteId);
      const pages = { ...(prev.site_float_actions_pages || {}) };
      delete pages[k];
      return { ...prev, site_float_actions_pages: pages };
    });
  }, []);

  const [catTotal, setCatTotal] = useState(0);
  const [copyingHashEntryId, setCopyingHashEntryId] = useState(0);
  const [movingCatalogSiteEntryId, setMovingCatalogSiteEntryId] = useState(0);
  const [catalogNotice, setCatalogNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(
    null
  );
  const [catPinDaysDraft, setCatPinDaysDraft] = useState<Record<number, number>>({});
  const [pinBusyEntryId, setPinBusyEntryId] = useState(0);
  const [refreshBusyEntryId, setRefreshBusyEntryId] = useState(0);
  const [soldBusyEntryId, setSoldBusyEntryId] = useState(0);
  const [catalogFormOpen, setCatalogFormOpen] = useState(false);
  const [catalogFormMode, setCatalogFormMode] = useState<'add' | 'edit'>('add');
  const [catalogFormSiteId, setCatalogFormSiteId] = useState(0);
  const [catalogFormEntryId, setCatalogFormEntryId] = useState(0);
  const [catalogFormTitle, setCatalogFormTitle] = useState('');
  const [catalogFormBody, setCatalogFormBody] = useState('');
  const [catalogFormPhone, setCatalogFormPhone] = useState('');
  const [catalogFormTypeKey, setCatalogFormTypeKey] = useState('');
  const [catalogFormEndDate, setCatalogFormEndDate] = useState('');
  const [catalogFormImages, setCatalogFormImages] = useState<string[]>([]);
  const [catalogFormLoading, setCatalogFormLoading] = useState(false);
  const [catalogFormSaving, setCatalogFormSaving] = useState(false);
  const [catalogDeleteBusyId, setCatalogDeleteBusyId] = useState(0);
  const [switcherItems, setSwitcherItems] = useState<Array<{ id: number; name: string; is_slave?: boolean; master_miniprogram_id?: number | null }>>([]);

  function renderSaveHint() {
    return (
      <>
        {err ? <p className={`${inlineErrorClass} mt-2`}>{err}</p> : null}
        {msg ? <p className={`${inlineSuccessClass} mt-2`}>{msg}</p> : null}
      </>
    );
  }

  const typeKeyOptionsForRow = useCallback(
    (currentKey: string) => {
      const t = mp?.types ?? [];
      const base = [{ key: '', label: '（未分类）' }, ...t.map((x) => ({ key: x.key, label: x.label }))];
      if (currentKey !== '' && !base.some((o) => o.key === currentKey)) {
        base.push({ key: currentKey, label: `${currentKey}（未在类型定义中）` });
      }
      return base;
    },
    [mp?.types]
  );

  /** 信息管理列表/筛选：含一级勾选后自动纳入的二级站点（与 get_miniprogram.entry_site_options 一致），并带层级展示文案 */
  const catalogSiteSelectRows = useMemo((): CatalogSiteOptionVM[] => {
    if (!mp) return [];
    const opts = mp.entry_site_options;
    let base: Array<{ id: number; site_name: string }>;
    if (Array.isArray(opts) && opts.length > 0) {
      base = opts
        .map((o) => ({
          id: typeof o.id === 'number' && !Number.isNaN(o.id) ? o.id : parseInt(String(o.id ?? 0), 10) || 0,
          site_name: typeof o.site_name === 'string' ? o.site_name : String(o.site_name ?? ''),
        }))
        .filter((o) => o.id > 0);
    } else {
      base = sites.filter((s) => mp.site_ids.includes(s.id)).map((s) => ({ id: s.id, site_name: s.site_name }));
    }
    return buildCatalogSiteOptionVMs(base, sites);
  }, [mp, sites]);

  const loadMp = useCallback(async () => {
    if (id < 1) return;
    setErr('');
    const r = await apiFetch<{ ok: boolean; miniprogram?: Miniprogram; err?: string }>('get_miniprogram', {
      method: 'GET',
      query: { id },
    });
    if (!r.ok || !r.miniprogram) {
      setErr('无法加载小程序：' + (r.err || 'unknown'));
      setMp(null);
      return;
    }
    const prog = r.miniprogram;
    const wecomRaw = prog.site_wecom_groups;
    const siteWecomGroups =
      wecomRaw && typeof wecomRaw === 'object' && !Array.isArray(wecomRaw)
        ? (wecomRaw as Record<string, SiteWecomGroupRow>)
        : {};
    const dialRaw = prog.phone_dial_reminder;
    const phoneDialReminder =
      dialRaw && typeof dialRaw === 'object'
        ? {
            enabled: !!dialRaw.enabled,
            title: String(dialRaw.title || '温馨提示'),
            body: String(dialRaw.body || ''),
            confirm_text: String(dialRaw.confirm_text || '确认拨打'),
          }
        : { enabled: false, title: '温馨提示', body: '', confirm_text: '确认拨打' };
    setMp({
      ...prog,
      site_wecom_groups: siteWecomGroups,
      phone_dial_reminder: phoneDialReminder,
      dial_limit: normalizeDialLimit(prog.dial_limit),
      ad_reward: normalizeAdReward(prog.ad_reward),
      features: normalizeMiniproFeatures(prog.features),
      site_minipro_features_pages: prog.site_minipro_features_pages || {},
      site_float_actions_pages: prog.site_float_actions_pages || {},
      site_subscribe_publish_pages: prog.site_subscribe_publish_pages || {},
      master_miniprogram_id: prog.master_miniprogram_id ?? null,
      master_name: prog.master_name ?? '',
      is_slave: !!prog.is_slave,
      master_site_ids_expanded: Array.isArray(prog.master_site_ids_expanded) ? prog.master_site_ids_expanded : [],
      publish_mode: normalizePublishMode(prog.publish_mode),
      mp_legal_docs: normalizeMpLegalDocs(prog.mp_legal_docs, prog.name),
      mp_user_self_settings: normalizeMpUserSelfSettings(prog.mp_user_self_settings),
    });
  }, [id]);

  const loadSites = useCallback(async () => {
    const r = await apiFetch<{ ok: boolean; items: SiteItem[] }>('list_sites', {
      method: 'GET',
      query: { page: 1, per_page: 100 },
    });
    if (r.ok) {
      setSites(r.items);
    }
  }, []);

  const loadSwitcher = useCallback(async () => {
    const r = await apiFetch<{
      ok: boolean;
      items: Array<{ id: number; name: string; is_slave?: boolean; master_miniprogram_id?: number | null }>;
    }>('list_miniprograms', { method: 'GET', query: { page: 1, per_page: 200 } });
    if (r.ok) {
      setSwitcherItems(r.items || []);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    if (id < 1) return;
    setErr('');
    const r = await apiFetch<{
      ok: boolean;
      items: CatalogRow[];
      total: number;
      page: number;
      per_page: number;
      catalog_sticky_sort_mode?: 'remaining_days' | 'refresh_time';
    }>('list_miniprogram_catalog', {
      method: 'GET',
      query: {
        miniprogram_id: id,
        page: catPage,
        per_page: CAT_PER,
        site_id: catSite > 0 ? catSite : undefined,
        q: catAppliedQ.trim() || undefined,
        type_key: catAppliedType || undefined,
      },
    });
    if (r.ok) {
      setCatItems(r.items);
      setCatTotal(r.total);
      if (r.catalog_sticky_sort_mode) {
        setMp((prev) =>
          prev ? { ...prev, catalog_sticky_sort_mode: r.catalog_sticky_sort_mode } : prev
        );
      }
    }
  }, [id, catPage, catSite, catAppliedQ, catAppliedType]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([loadMp(), loadSites(), loadSwitcher()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMp, loadSites, loadSwitcher]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  async function saveMeta() {
    if (!mp) return;
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const typesPayload = ensureMiniproTypeKeys(mp.types);
      if (!mp.is_slave && typesPayload.some((t) => !String(t.label || '').trim())) {
        setErr('请为每个信息类型填写显示名，或删除空白行后再保存。');
        return;
      }
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram', {
        method: 'POST',
        body: JSON.stringify({
          id: mp.id,
          master_miniprogram_id: mp.master_miniprogram_id && mp.master_miniprogram_id > 0 ? mp.master_miniprogram_id : 0,
          name: mp.name,
          enabled: mp.enabled,
          nav_title: mp.nav_title,
          share_title: mp.share_title,
          share_image_path: mp.share_image_path || '',
          slides: mp.slides,
          site_ids: mp.site_ids,
          types: typesPayload,
          site_wecom_groups: mp.site_wecom_groups || {},
          auto_heat: mp.auto_heat,
          detail_notice: mp.detail_notice,
          phone_contact_hint_template: mp.phone_contact_hint_template ?? '',
          phone_dial_reminder: mp.phone_dial_reminder ?? {
            enabled: false,
            title: '温馨提示',
            body: '',
            confirm_text: '确认拨打',
          },
          dial_limit: normalizeDialLimit(mp.dial_limit),
          ad_reward: normalizeAdReward(mp.ad_reward),
          features: normalizeMiniproFeatures(mp.features),
          site_minipro_features_pages: mp.site_minipro_features_pages || {},
          site_float_actions_pages: mp.site_float_actions_pages || {},
          list_increment_views_enabled: mp.list_increment_views_enabled,
          list_site_name_with_parent_enabled: mp.list_site_name_with_parent_enabled !== false,
          share_actions_enabled: mp.share_actions_enabled,
          detail_share_image_mode: mp.detail_share_image_mode || 'cover',
          list_page_size: mp.list_page_size,
          hot_search_max_len: mp.hot_search_max_len ?? 4,
          hot_search_allow_symbols: !!mp.hot_search_allow_symbols,
          wechat_miniprogram_appid: mp.wechat_miniprogram_appid ?? '',
          wechat_miniprogram_appsecret: mp.wechat_miniprogram_appsecret ?? '',
          footer_tab_home_enabled: mp.footer_tab_home_enabled !== false,
          footer_tab_subscribe_enabled: !!mp.footer_tab_subscribe_enabled,
          footer_tab_publish_enabled: !!mp.footer_tab_publish_enabled,
          site_master_portal_enabled: !!mp.site_master_portal_enabled,
          site_master_portal_tap_count: Math.min(
            20,
            Math.max(3, Number(mp.site_master_portal_tap_count) || 8)
          ),
          subscribe_mp_qr_path: mp.subscribe_mp_qr_path ?? '',
          subscribe_title: mp.subscribe_title ?? '',
          subscribe_body: mp.subscribe_body ?? '',
          publish_service_qr_path: mp.publish_service_qr_path ?? '',
          publish_title: mp.publish_title ?? '',
          publish_body: mp.publish_body ?? '',
          catalog_sticky_sort_mode: mp.catalog_sticky_sort_mode === 'refresh_time' ? 'refresh_time' : 'remaining_days',
          site_subscribe_publish_pages: mp.site_subscribe_publish_pages || {},
          publish_mode: normalizePublishMode(mp.publish_mode),
          mp_legal_docs: normalizeMpLegalDocs(mp.mp_legal_docs, mp.name),
          mp_user_self_settings: normalizeMpUserSelfSettings(mp.mp_user_self_settings),
        }),
      });
      if (!r.ok) {
        setErr('保存失败：' + (r.err || 'unknown'));
        return;
      }
      setMsg('已保存');
      await loadMp();
    } finally {
      setSaving(false);
    }
  }

  async function copyMiniProgramHashLink(entryId: number) {
    if (!mp || entryId < 1) return;
    if (!mp.wechat_miniprogram_appid || !/^wx[a-zA-Z0-9]{16}$/.test(String(mp.wechat_miniprogram_appid).trim())) {
      setErr('请先在上方填写并保存「公众号草稿：小程序 AppID」。');
      return;
    }
    if (!mp.wechat_miniprogram_appsecret || String(mp.wechat_miniprogram_appsecret).trim().length < 16) {
      setErr('请先在上方填写并保存「微信内 #小程序 链接：小程序 AppSecret」。');
      return;
    }
    setCopyingHashEntryId(entryId);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; hash_link?: string; detail?: string; err?: string }>('get_entry_miniprogram_hash_link', {
        method: 'GET',
        query: { entry_id: entryId, miniprogram_id: mp.id },
      });
      if (!r.ok || !r.hash_link) {
        setErr(r.detail || r.err || '未获取到 #小程序:// 链接');
        return;
      }
      try {
        await navigator.clipboard.writeText(r.hash_link);
      } catch {
        setErr('复制失败：浏览器不支持剪贴板写入，请手动复制。');
        return;
      }
      setMsg(`已复制：${r.hash_link}`);
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '请求失败');
    } finally {
      setCopyingHashEntryId(0);
    }
  }

  function toggleSite(sid: number) {
    if (!mp) return;
    const set = new Set(mp.site_ids);
    const turningOn = !set.has(sid);
    if (turningOn && mp.is_slave && (mp.master_site_ids_expanded?.length ?? 0) > 0) {
      const allow = mp.master_site_ids_expanded ?? [];
      if (!allow.includes(sid)) {
        return;
      }
    }
    const groups = { ...(mp.site_wecom_groups || {}) };
    if (set.has(sid)) {
      set.delete(sid);
      delete groups[String(sid)];
    } else {
      set.add(sid);
    }
    setMp({ ...mp, site_ids: Array.from(set).sort((a, b) => a - b), site_wecom_groups: groups });
  }

  function addSlide() {
    if (!mp) return;
    setMp({ ...mp, slides: [...mp.slides, { image_url: '', title: '' }] });
  }

  function updateSlide(i: number, patch: Partial<SlideRow>) {
    if (!mp) return;
    const slides = mp.slides.map((s, j) => (j === i ? { ...s, ...patch } : s));
    setMp({ ...mp, slides });
  }

  function removeSlide(i: number) {
    if (!mp) return;
    setMp({ ...mp, slides: mp.slides.filter((_, j) => j !== i) });
  }

  function addTypeRow() {
    if (!mp) return;
    const used = new Set(
      mp.types.map((x) => (x.key || '').trim()).filter((k) => /^[a-z0-9_]{1,48}$/.test(k))
    );
    const key = generateMiniproTypeKey(used);
    setMp({ ...mp, types: [...mp.types, { key, label: '' }] });
  }

  function updateTypeRow(i: number, patch: Partial<TypeRow>) {
    if (!mp) return;
    const types = mp.types.map((t, j) => (j === i ? { ...t, ...patch } : t));
    setMp({ ...mp, types });
  }

  function removeTypeRow(i: number) {
    if (!mp) return;
    setMp({ ...mp, types: mp.types.filter((_, j) => j !== i) });
  }

  function moveTypeUp(i: number) {
    if (!mp || i < 1 || i >= mp.types.length) return;
    const types = mp.types.slice();
    const t = types[i - 1];
    types[i - 1] = types[i]!;
    types[i] = t!;
    setMp({ ...mp, types });
  }

  function moveTypeDown(i: number) {
    if (!mp || i < 0 || i >= mp.types.length - 1) return;
    const types = mp.types.slice();
    const t = types[i + 1];
    types[i + 1] = types[i]!;
    types[i] = t!;
    setMp({ ...mp, types });
  }

  function applyHeatPreset(preset: 'conservative' | 'standard' | 'aggressive') {
    if (!mp) return;
    const p = HEAT_PRESETS[preset];
    setMp({
      ...mp,
      auto_heat: {
        ...(mp.auto_heat || {
          enabled: false,
          dedupe_window_seconds: 14400,
          heat_probability_percent: 35,
          heat_min_increment: 1,
          heat_max_increment: 3,
          heat_daily_cap: 200,
        }),
        dedupe_window_seconds: p.dedupe_window_seconds,
        heat_probability_percent: p.heat_probability_percent,
        heat_min_increment: p.heat_min_increment,
        heat_max_increment: p.heat_max_increment,
        heat_daily_cap: p.heat_daily_cap,
      },
    });
    setHeatPreset(preset);
    setMsg('已应用运营预设（请点击保存浏览量策略）');
  }

  function applyDetailNoticePreset(preset: 'resale' | 'hiring' | 'marriage') {
    if (!mp) return;
    const p = DETAIL_NOTICE_PRESETS[preset];
    setMp({
      ...mp,
      detail_notice: {
        ...(mp.detail_notice || { enabled: true, title: '', items: [], more_tip: '' }),
        enabled: true,
        title: p.title,
        items: [...p.items],
        more_tip: p.more_tip,
      },
    });
    setDetailNoticePreset(preset);
    setMsg('已应用详情页文案预设（请点击保存详情页文案）');
  }

  async function onPickTypeDefaultCover(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      updateTypeRow(i, { default_cover_path: path });
      setMsg('已上传默认图（请点击「保存类型定义」写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickTypeReviewQr(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      updateTypeRow(i, { review_service_qr_path: path });
      setMsg('已上传客服二维码（请点击「保存类型定义」写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickTypePinServiceQr(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      updateTypeRow(i, { service_qr_path: path });
      setMsg('已上传该类型置顶客服二维码（请点击「保存类型定义」写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickShare(e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      setMp({ ...mp, share_image_path: path });
      setMsg('分享配图已更新（请点击保存写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickSubscribeQr(e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      setMp({ ...mp, subscribe_mp_qr_path: path });
      setMsg('公众号二维码已填入（请点击保存写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickPublishQr(e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      setMp({ ...mp, publish_service_qr_path: path });
      setMsg('客服二维码已填入（请点击保存写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickSubscribeQrSite(siteId: number, e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || id < 1) return;
    try {
      const path = await uploadMiniproImage(id, f);
      siteSubPubPatch(siteId, { subscribe_mp_qr_path: path });
      setMsg('公众号二维码已填入该站点覆盖项（请点击保存写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickPublishQrSite(siteId: number, e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || id < 1) return;
    try {
      const path = await uploadMiniproImage(id, f);
      siteSubPubPatch(siteId, { publish_service_qr_path: path });
      setMsg('客服二维码已填入该站点覆盖项（请点击保存写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickFloatQrSite(siteId: number, kind: 'publish' | 'promo', file: File) {
    if (!mp || id < 1) return;
    try {
      const path = await uploadMiniproImage(mp.id, file);
      siteFloatPatch(siteId, kind === 'publish' ? { publish_qr_path: path } : { promo_qr_path: path });
      setMsg('悬浮钮二维码已填入该站点覆盖项（请点击保存运营扩展配置）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function onPickSlide(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    if (!mp) return;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await uploadMiniproImage(mp.id, f);
      updateSlide(i, { image_url: path });
      setMsg('幻灯片图片已填入（请点击保存写入）');
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function setEntryType(entryId: number, typeKey: string) {
    if (!mp) return;
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('set_miniprogram_entry_type', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mp.id, entry_id: entryId, type_key: typeKey }),
      });
      if (!r.ok) {
        setErr(r.err || '更新分类失败');
        return;
      }
      await loadCatalog();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '请求失败');
    }
  }

  async function setEntryPin(entryId: number, pinDays: number) {
    if (!mp) return;
    setPinBusyEntryId(entryId);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('set_miniprogram_entry_pin', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mp.id, entry_id: entryId, pin_days: pinDays }),
      });
      if (!r.ok) {
        setErr(r.err || '置顶设置失败');
        return;
      }
      setMsg(pinDays < 1 ? '已取消置顶' : '置顶已更新');
      await loadCatalog();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '请求失败');
    } finally {
      setPinBusyEntryId(0);
    }
  }

  async function touchEntryRefresh(entryId: number) {
    if (!mp) return;
    setRefreshBusyEntryId(entryId);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('touch_miniprogram_entry_refresh', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mp.id, entry_id: entryId }),
      });
      if (!r.ok) {
        setErr(r.err || '刷新失败');
        return;
      }
      setMsg('已更新刷新时间');
      await loadCatalog();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '请求失败');
    } finally {
      setRefreshBusyEntryId(0);
    }
  }

  async function setEntrySoldOut(entryId: number, soldOut: boolean) {
    if (!mp) return;
    setSoldBusyEntryId(entryId);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('set_miniprogram_entry_sold_out', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mp.id, entry_id: entryId, sold_out: soldOut }),
      });
      if (!r.ok) {
        setErr(r.err || '更新已售状态失败');
        return;
      }
      setMsg(soldOut ? '已标为已售（已从转发任务队列移除）' : '已恢复为在售');
      await loadCatalog();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '请求失败');
    } finally {
      setSoldBusyEntryId(0);
    }
  }

  function defaultCatalogFormSiteId(): number {
    if (catSite > 0) return catSite;
    return catalogSiteSelectRows[0]?.id ?? 0;
  }

  function openCatalogAdd() {
    if (!mp) return;
    if (catalogSiteSelectRows.length === 0) {
      setErr('请先在「参数配置」中勾选展示站点并保存');
      return;
    }
    setCatalogFormMode('add');
    setCatalogFormEntryId(0);
    setCatalogFormSiteId(defaultCatalogFormSiteId());
    setCatalogFormTitle('');
    setCatalogFormBody('');
    setCatalogFormPhone('');
    setCatalogFormTypeKey(catAppliedType || mp.types?.[0]?.key || '');
    setCatalogFormEndDate('');
    setCatalogFormImages([]);
    setCatalogFormOpen(true);
  }

  async function openCatalogEdit(row: CatalogRow) {
    if (!mp) return;
    setCatalogFormMode('edit');
    setCatalogFormEntryId(row.id);
    setCatalogFormSiteId(row.site_id);
    setCatalogFormTitle(row.title_text ?? '');
    setCatalogFormBody(row.body_text ?? '');
    setCatalogFormPhone('');
    setCatalogFormTypeKey(row.type_key ?? '');
    setCatalogFormEndDate('');
    setCatalogFormImages([]);
    setCatalogFormOpen(true);
    setCatalogFormLoading(true);
    try {
      const r = await apiFetch<{
        ok: boolean;
        entry?: {
          title_text?: string | null;
          body_text?: string | null;
          contact_phone?: string | null;
          valid_end_date?: string;
          images?: { local_path: string; sort_order: number }[];
        };
      }>('get_entry', { method: 'GET', query: { id: row.id, site_id: row.site_id } });
      if (r.ok && r.entry) {
        setCatalogFormTitle(r.entry.title_text ?? '');
        setCatalogFormBody(r.entry.body_text ?? '');
        setCatalogFormPhone(r.entry.contact_phone ?? '');
        setCatalogFormEndDate((r.entry.valid_end_date ?? '').slice(0, 10));
        setCatalogFormImages(
          (r.entry.images ?? [])
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((im) => im.local_path?.trim() ?? '')
            .filter(Boolean)
        );
      } else {
        setErr('加载信息失败');
        setCatalogFormOpen(false);
      }
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '加载失败');
      setCatalogFormOpen(false);
    } finally {
      setCatalogFormLoading(false);
    }
  }

  async function saveCatalogForm() {
    if (!mp) return;
    const title = catalogFormTitle.trim();
    const body = catalogFormBody.trim();
    if (title === '' && body === '') {
      setErr('请填写标题或正文');
      return;
    }
    if (catalogFormSiteId < 1) {
      setErr('请选择所属站点');
      return;
    }
    setCatalogFormSaving(true);
    setErr('');
    setMsg('');
    try {
      if (catalogFormMode === 'add') {
        const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; entry_id?: number }>(
          'create_manual_entry',
          {
            method: 'POST',
            body: JSON.stringify({
              site_id: catalogFormSiteId,
              miniprogram_id: mp.id,
              type_key: catalogFormTypeKey,
              title_text: title,
              body_text: body,
              contact_phone: catalogFormPhone.trim(),
              image_local_paths: catalogFormImages.length > 0 ? catalogFormImages : undefined,
            }),
          }
        );
        if (!r.ok) {
          setErr(r.detail ?? r.err ?? '添加失败');
          return;
        }
        setMsg('信息已添加');
      } else {
        const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('update_entry', {
          method: 'POST',
          body: JSON.stringify({
            site_id: catalogFormSiteId,
            entry_id: catalogFormEntryId,
            miniprogram_id: mp.id,
            type_key: catalogFormTypeKey,
            title_text: title,
            body_text: body,
            contact_phone: catalogFormPhone,
            valid_end_date: catalogFormEndDate || undefined,
            image_local_paths: catalogFormImages,
          }),
        });
        if (!r.ok) {
          setErr(r.detail ?? r.err ?? '保存失败');
          return;
        }
        setMsg('已保存');
      }
      setCatalogFormOpen(false);
      await loadCatalog();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '保存失败');
    } finally {
      setCatalogFormSaving(false);
    }
  }

  async function deleteCatalogEntry(row: CatalogRow) {
    if (!window.confirm(`确定删除信息 #${row.id}？删除后不可恢复。`)) return;
    setCatalogDeleteBusyId(row.id);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('delete_entry', {
        method: 'POST',
        body: JSON.stringify({ entry_id: row.id, site_id: row.site_id }),
      });
      if (r.ok) {
        setMsg('已删除');
        await loadCatalog();
      } else {
        setErr(r.err ?? '删除失败');
      }
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '删除失败');
    } finally {
      setCatalogDeleteBusyId(0);
    }
  }

  async function saveCatalogStickySort() {
    if (!mp) return;
    setErr('');
    setMsg('');
    try {
      const mode = mp.catalog_sticky_sort_mode === 'refresh_time' ? 'refresh_time' : 'remaining_days';
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_catalog_sticky_sort', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mp.id, catalog_sticky_sort_mode: mode }),
      });
      if (!r.ok) {
        setErr(r.err || '保存失败');
        return;
      }
      setMp((p) => (p ? { ...p, catalog_sticky_sort_mode: mode } : p));
      setMsg('已保存置顶排序方式');
      await loadCatalog();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : '请求失败');
    }
  }

  async function setEntrySite(entryId: number, newSiteId: number, prevSiteId: number) {
    if (!mp) return;
    if (newSiteId < 1 || newSiteId === prevSiteId) return;
    const target = catalogSiteSelectRows.find((s) => s.id === newSiteId);
    const targetLabel = target?.treeLabel ?? `站点 #${newSiteId}`;
    if (
      !window.confirm(
        `确定将信息 #${entryId} 迁入「${targetLabel}」？\n将在目标站创建新信息，并删除原站信息及其转发任务。`
      )
    ) {
      return;
    }
    setMovingCatalogSiteEntryId(entryId);
    setCatalogNotice(null);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<EntryRelocateResponse>('move_entry_site', {
        method: 'POST',
        body: JSON.stringify({ entry_id: entryId, new_site_id: newSiteId, miniprogram_id: mp.id }),
      });
      if (!r.ok) {
        const text =
          r.err === 'dedupe_conflict'
            ? r.detail || '目标站点已有相同金数据幂等键'
            : r.err === 'miniprogram_site_scope'
              ? r.detail || '须选择本小程序已勾选的展示站点'
              : r.detail || r.err || '更换站点失败';
        setCatalogNotice({ kind: 'error', text });
        return;
      }
      const noticeText = formatEntryRelocateMessage(r, targetLabel);
      setCatalogNotice({ kind: 'success', text: noticeText });
      setCatItems((prev) => prev.filter((x) => x.id !== entryId));
      if (catSite > 0 && catSite === prevSiteId) {
        setCatSite(0);
      }
      await loadCatalog();
    } catch (ex: unknown) {
      setCatalogNotice({
        kind: 'error',
        text: ex instanceof Error ? ex.message : '请求失败',
      });
    } finally {
      setMovingCatalogSiteEntryId(0);
      void loadCatalog();
    }
  }

  if (loading) {
    return <p className="p-6 text-slate-600">加载中…</p>;
  }
  if (!mp) {
    return (
      <div className="space-y-4 p-6">
        <p className={inlineErrorClass}>{err || '记录不存在'}</p>
        <Link className="text-sky-700 underline" to="/miniprograms">
          返回列表
        </Link>
      </div>
    );
  }

  const scopedSites = sites.filter((s) => mp.site_ids.includes(s.id));

  return (
    <div className="space-y-6">
      <PageHeader
        title={`编辑：${mp.name}`}
        description={`小程序 ID（写入微信小程序 config）：${mp.public_id}${isSysAdmin ? '。系统管理员可代管任意账号的小程序。' : ''}`}
        actions={
          <Link
            to={`/miniprograms?active_id=${id}&active_view=${pageView}`}
            className="text-sm font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900"
          >
            返回列表
          </Link>
        }
      />
      {switcherItems.length > 1 ? (
        <div className="rounded-xl border border-slate-200/90 bg-gradient-to-r from-slate-50 to-white p-4 shadow-sm">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">快速切换</span>
            {mp.is_slave && mp.master_name ? (
              <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-900">
                从属：{mp.master_name}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {switcherItems.map((it) => {
              const active = it.id === id;
              const slaveTag = it.is_slave ? '从' : '主';
              return (
                <Link
                  key={it.id}
                  to={`/miniprograms/${it.id}?view=${pageView}`}
                  className={`inline-flex max-w-[220px] items-center gap-1.5 truncate rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    active
                      ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  title={it.name}
                >
                  <span className="shrink-0 rounded bg-white/15 px-1 text-[10px] font-bold uppercase text-current">{slaveTag}</span>
                  <span className="truncate">{it.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-600">将“参数配置”和“信息管理”分开展示，便于日常运营。</div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            <button
              type="button"
              onClick={() => switchView('config')}
              className={`rounded-md px-4 py-2 text-sm font-medium ${pageView === 'config' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              参数配置
            </button>
            <button
              type="button"
              onClick={() => switchView('types')}
              className={`rounded-md px-4 py-2 text-sm font-medium ${pageView === 'types' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              信息类型管理
            </button>
            <button
              type="button"
              onClick={() => switchView('ads')}
              className={`rounded-md px-4 py-2 text-sm font-medium ${pageView === 'ads' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              广告管理
            </button>
            <button
              type="button"
              onClick={() => switchView('content')}
              className={`rounded-md px-4 py-2 text-sm font-medium ${pageView === 'content' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              信息管理
            </button>
          </div>
        </div>
      </Card>

      {pageView === 'config' ? (
        <>
      <MiniprogramPublishModeCard
        miniprogramId={mp.id}
        platformName={mp.name}
        publishMode={normalizePublishMode(mp.publish_mode)}
        legalDocs={normalizeMpLegalDocs(mp.mp_legal_docs, mp.name)}
        onChange={(patch) =>
          setMp((prev) =>
            prev
              ? {
                  ...prev,
                  ...(patch.publish_mode !== undefined ? { publish_mode: patch.publish_mode } : {}),
                  ...(patch.mp_legal_docs !== undefined ? { mp_legal_docs: patch.mp_legal_docs } : {}),
                }
              : prev,
          )
        }
      />
      {normalizePublishMode(mp.publish_mode) === 'user_self' ? (
        <>
        <MiniprogramReviewSettingsCard
          settings={normalizeMpUserSelfSettings(mp.mp_user_self_settings)}
          onChange={(patch) =>
            setMp((prev) =>
              prev
                ? {
                    ...prev,
                    mp_user_self_settings: {
                      ...normalizeMpUserSelfSettings(prev.mp_user_self_settings),
                      ...patch,
                    },
                  }
                : prev,
            )
          }
        />
        <MiniprogramWecomPushCard
          settings={normalizeMpUserSelfWecomPush(normalizeMpUserSelfSettings(mp.mp_user_self_settings).wecom_push)}
          onChange={(patch) =>
            setMp((prev) =>
              prev
                ? {
                    ...prev,
                    mp_user_self_settings: {
                      ...normalizeMpUserSelfSettings(prev.mp_user_self_settings),
                      wecom_push: {
                        ...normalizeMpUserSelfWecomPush(
                          normalizeMpUserSelfSettings(prev.mp_user_self_settings).wecom_push,
                        ),
                        ...patch,
                      },
                    },
                  }
                : prev,
            )
          }
        />
        </>
      ) : null}
      <Card title="主从关系">
        <p className="mb-3 text-sm text-slate-600">
          将当前小程序设为<strong className="text-slate-800">从属小程序</strong>后，其<strong>信息类型</strong>将与主小程序完全一致（在「信息类型管理」中不可单独编辑）；<strong>展示站点</strong>仅能勾选主小程序已包含的站点。主从集群内<strong>信息类型标注</strong>（每条信息的类型）多端共享。
        </p>
        <label className={labelClass}>隶属主小程序</label>
        <select
          className={`${inputClass} max-w-xl`}
          value={mp.master_miniprogram_id && mp.master_miniprogram_id > 0 ? String(mp.master_miniprogram_id) : ''}
          onChange={(e) => {
            const v = e.target.value === '' ? null : Number(e.target.value);
            setMp({ ...mp, master_miniprogram_id: v });
          }}
        >
          <option value="">独立小程序（无主从，可作为主）</option>
          {switcherItems
            .filter((x) => !x.is_slave && x.id !== mp.id)
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}（ID {x.id}）
              </option>
            ))}
        </select>
        <p className="mt-2 text-xs text-slate-500">
          若本小程序下已有其他从属小程序，则不能再设为从属。保存后请再点「保存站点选择」以按主小程序范围收敛展示站点。
        </p>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存主从设置
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="基础与转发">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>名称</label>
            <input className={inputClass} value={mp.name} onChange={(e) => setMp({ ...mp, name: e.target.value })} />
          </div>
          <div className="flex items-end gap-3 pb-1">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={mp.enabled} onChange={(e) => setMp({ ...mp, enabled: e.target.checked })} />
              启用（停用后小程序端拉取为 404）
            </label>
          </div>
          <div>
            <label className={labelClass}>导航栏标题</label>
            <input
              className={inputClass}
              value={mp.nav_title}
              onChange={(e) => setMp({ ...mp, nav_title: e.target.value })}
              placeholder="默认可与名称一致"
            />
          </div>
          <div>
            <label className={labelClass}>转发分享标题</label>
            <input className={inputClass} value={mp.share_title} onChange={(e) => setMp({ ...mp, share_title: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>列表每页条数（5～50）</label>
            <input
              type="number"
              min={5}
              max={50}
              className={inputClass}
              value={mp.list_page_size}
              onChange={(e) => setMp({ ...mp, list_page_size: Math.min(50, Math.max(5, Number(e.target.value) || 20)) })}
            />
          </div>
          <div>
            <label className={labelClass}>常搜词最大长度（2～24）</label>
            <input
              type="number"
              min={2}
              max={24}
              className={inputClass}
              value={mp.hot_search_max_len ?? 4}
              onChange={(e) => setMp({ ...mp, hot_search_max_len: Math.min(24, Math.max(2, Number(e.target.value) || 4)) })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!mp.hot_search_allow_symbols}
                onChange={(e) => setMp({ ...mp, hot_search_allow_symbols: e.target.checked })}
              />
              <span>常搜词允许符号与标点（关闭时仅允许汉字和字母，默认关闭）</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>公众号草稿：小程序 AppID（可选）</label>
            <p className="mt-1 text-xs text-slate-500">
              与微信公众平台「公众号 → 广告与小程序 → 小程序管理」中关联的同一小程序一致；格式如 wx1a2b3c4d5e6f7g8h。填写后，凡本站已勾选进本小程序「数据站点」的站点，在推送公众号草稿时可生成进入{' '}
              <code className="rounded bg-slate-100 px-1">pages/detail/detail?id=条目ID</code> 的文字链（详见「内容与样式」说明）。
            </p>
            <input
              className={`${inputClass} mt-2 max-w-md font-mono text-sm`}
              value={mp.wechat_miniprogram_appid ?? ''}
              onChange={(e) => setMp({ ...mp, wechat_miniprogram_appid: e.target.value.trim() })}
              placeholder="wx 开头 18 位，留空则不生成公众号小程序链"
              spellCheck={false}
            />
            <label className={`${labelClass} mt-3`}>微信内 #小程序 链接：小程序 AppSecret（可选）</label>
            <p className="mt-1 text-xs text-slate-500">
              配置后可在「条目分类」中点击“小程序链接(#)”直接生成并复制 <code className="rounded bg-slate-100 px-1">#小程序://名称/短码</code>（不依赖公众号草稿）。
            </p>
            <input
              className={`${inputClass} mt-2 max-w-md font-mono text-sm`}
              type="password"
              value={mp.wechat_miniprogram_appsecret ?? ''}
              onChange={(e) => setMp({ ...mp, wechat_miniprogram_appsecret: e.target.value.trim() })}
              placeholder="填写小程序 AppSecret，留空则不启用 #小程序 链接按钮"
              spellCheck={false}
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={mp.list_increment_views_enabled}
                onChange={(e) => setMp({ ...mp, list_increment_views_enabled: e.target.checked })}
              />
              <span>
                列表加载时增加浏览量：每次拉取列表接口，对本页每条信息在服务端 +1（适合冷启动阶段；关闭后仅详情打开计浏览量）
              </span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">详情页转发卡片配图</label>
            <select
              className="select-base w-full max-w-md"
              value={mp.detail_share_image_mode || 'cover'}
              onChange={(e) =>
                setMp({
                  ...mp,
                  detail_share_image_mode: e.target.value as 'cover' | 'title' | 'cover_title',
                })
              }
            >
              <option value="cover">采用配图（信息首图或默认分享图）</option>
              <option value="title">采用标题（生成标题分享图）</option>
              <option value="cover_title">封面+标题（配图作背景，标题叠加显示）</option>
            </select>
            <p className="mt-1.5 text-xs text-slate-500">
              详情页「转给好友 / 分享朋友圈」时使用。选「采用配图」时无首图则使用上方默认分享配图；选「封面+标题」时无首图则退化为纯标题图。
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={mp.share_actions_enabled}
                onChange={(e) => setMp({ ...mp, share_actions_enabled: e.target.checked })}
              />
              <span>
                详情页显示分享栏（转给好友 / 分享朋友圈）。关闭后详情页不显示任何分享入口（右上角菜单仍受微信系统控制）。
              </span>
            </label>
          </div>
          <div className="md:col-span-2 rounded-lg border border-teal-200/80 bg-teal-50/50 p-3">
            <p className="mb-2 text-xs font-semibold text-teal-900">小程序列表站点名（首页 / 详情底栏）</p>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={mp.list_site_name_with_parent_enabled !== false}
                onChange={(e) => setMp({ ...mp, list_site_name_with_parent_enabled: e.target.checked })}
              />
              <span>
                二级站点显示为「一级.二级」（如 南京站.栖霞区）；取消勾选则仅显示二级名（如 栖霞区）。一级站点名称不变。
              </span>
            </label>
          </div>
        </div>
        <div className="mt-4">
          <label className={labelClass}>转发分享配图</label>
          <p className="mt-1 text-xs text-slate-500">建议使用 5:4 比例（例如 1000×800），转发展示更完整。</p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {mp.share_image_path ? (
              <img src={resolveMediaUrl(mp.share_image_path)} alt="" className="h-20 w-[100px] rounded-lg border border-slate-200 object-cover" />
            ) : (
              <span className="text-sm text-slate-500">未设置</span>
            )}
            <label className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">
              上传图片
              <input type="file" accept="image/*" className="hidden" onChange={(e) => void onPickShare(e)} />
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            {saving ? '保存中…' : '保存本页配置'}
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="底部导航与导流页（小程序）">
        <p className="mb-4 text-sm text-slate-600">
          在小程序底部显示「首页 / 订阅 / 发布」入口；关闭某项后客户端不再显示该按钮，且无法通过小程序链接直达对应导流页（将自动进入首页）。至少开启两项时才会出现底栏（仅一项时不占空间）。
          订阅/发布页的<strong className="font-medium text-slate-800">文案与二维码</strong>支持按下方「展示站点」单独覆盖；未配置站点或选「全部」时使用上方全站默认。
        </p>
        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={mp.footer_tab_home_enabled !== false}
              onChange={(e) => setMp({ ...mp, footer_tab_home_enabled: e.target.checked })}
            />
            显示「首页」（当前信息列表页）
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={!!mp.footer_tab_subscribe_enabled}
              onChange={(e) => setMp({ ...mp, footer_tab_subscribe_enabled: e.target.checked })}
            />
            显示「订阅」（公众号二维码与说明）
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={!!mp.footer_tab_publish_enabled}
              onChange={(e) => setMp({ ...mp, footer_tab_publish_enabled: e.target.checked })}
            />
            显示「发布」（客服二维码与说明）
          </label>
        </div>
        <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50/50 p-4">
          <h3 className="text-sm font-semibold text-violet-950">站长管理（小程序隐藏入口）</h3>
          <p className="mt-1 text-xs leading-relaxed text-violet-900/90">
            开启后，站长可在小程序「发布」页<strong>连续点击</strong>下方说明卡片（pub-hero）指定次数，静默打开与
            <code className="rounded bg-white/80 px-1">/w/login</code> 相同的手机端站长界面（须已在 config.php 配置{' '}
            <code className="rounded bg-white/80 px-1">site_url</code>，且该域名已加入小程序业务域名）。过程中无任何提示，普通用户无感知。
          </p>
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={!!mp.site_master_portal_enabled}
              onChange={(e) => setMp({ ...mp, site_master_portal_enabled: e.target.checked })}
            />
            启用发布页隐藏入口
          </label>
          <div className="mt-3 max-w-xs">
            <label className={labelClass}>连续点击次数（3～20，默认 8）</label>
            <input
              type="number"
              min={3}
              max={20}
              className={inputClass}
              disabled={!mp.site_master_portal_enabled}
              value={mp.site_master_portal_tap_count ?? 8}
              onChange={(e) =>
                setMp({
                  ...mp,
                  site_master_portal_tap_count: Math.min(20, Math.max(3, Number(e.target.value) || 8)),
                })
              }
            />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">订阅页（公众号）</h3>
            <label className={labelClass}>页面标题</label>
            <input
              className={inputClass}
              value={mp.subscribe_title ?? ''}
              onChange={(e) => setMp({ ...mp, subscribe_title: e.target.value })}
              placeholder="例如：关注贴图虾公众号"
            />
            <label className={`${labelClass} mt-3`}>说明文字</label>
            <textarea
              className={`${inputClass} min-h-[100px]`}
              value={mp.subscribe_body ?? ''}
              onChange={(e) => setMp({ ...mp, subscribe_body: e.target.value })}
              placeholder="引导用户关注、获取通知等"
            />
            <label className={`${labelClass} mt-3`}>公众号二维码</label>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {mp.subscribe_mp_qr_path ? (
                <img
                  src={resolveMediaUrl(mp.subscribe_mp_qr_path)}
                  alt=""
                  className="h-28 w-28 rounded-lg border border-slate-200 object-cover"
                />
              ) : (
                <span className="text-sm text-slate-500">未上传</span>
              )}
              <label className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">
                上传图片
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void onPickSubscribeQr(e)} />
              </label>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">发布页（客服）</h3>
            <label className={labelClass}>页面标题</label>
            <input
              className={inputClass}
              value={mp.publish_title ?? ''}
              onChange={(e) => setMp({ ...mp, publish_title: e.target.value })}
              placeholder="例如：联系客服发布信息"
            />
            <label className={`${labelClass} mt-3`}>说明文字</label>
            <textarea
              className={`${inputClass} min-h-[100px]`}
              value={mp.publish_body ?? ''}
              onChange={(e) => setMp({ ...mp, publish_body: e.target.value })}
              placeholder="服务时间、添加方式、投稿须知等"
            />
            <label className={`${labelClass} mt-3`}>客服二维码</label>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {mp.publish_service_qr_path ? (
                <img
                  src={resolveMediaUrl(mp.publish_service_qr_path)}
                  alt=""
                  className="h-28 w-28 rounded-lg border border-slate-200 object-cover"
                />
              ) : (
                <span className="text-sm text-slate-500">未上传</span>
              )}
              <label className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">
                上传图片
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void onPickPublishQr(e)} />
              </label>
            </div>
          </div>
        </div>
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-slate-50/90 p-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-900">按一级 / 二级站点单独配置（可选）</h3>
          <p className="mb-4 text-xs leading-relaxed text-slate-600">
            小程序在首页选中站点后，「订阅 / 发布」按站点生效；二级默认沿用所属一级，字段留空则沿用上级。底部导航开关仍为全站统一。
          </p>
          {scopedSites.length === 0 ? (
            <p className="text-sm text-amber-800">请先在上方勾选展示站点并保存后，再为各站填写覆盖项。</p>
          ) : (
            <SiteSubscribePublishHierarchyEditor
              sites={sites}
              siteIds={mp.site_ids || []}
              pages={mp.site_subscribe_publish_pages || {}}
              onPatch={siteSubPubPatch}
              onClear={siteSubPubClear}
              onPickSubscribeQr={(siteId, file) => {
                void (async () => {
                  try {
                    const path = await uploadMiniproImage(id, file);
                    siteSubPubPatch(siteId, { subscribe_mp_qr_path: path });
                    setMsg('公众号二维码已填入该站点（请点击保存本页配置）');
                  } catch (ex: unknown) {
                    setErr(ex instanceof Error ? ex.message : '上传失败');
                  }
                })();
              }}
              onPickPublishQr={(siteId, file) => {
                void (async () => {
                  try {
                    const path = await uploadMiniproImage(id, file);
                    siteSubPubPatch(siteId, { publish_service_qr_path: path });
                    setMsg('客服二维码已填入该站点（请点击保存本页配置）');
                  } catch (ex: unknown) {
                    setErr(ex instanceof Error ? ex.message : '上传失败');
                  }
                })();
              }}
            />
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            {saving ? '保存中…' : '保存本页配置'}
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="浏览量策略（自动加热）">
        <p className="mb-3 text-sm text-slate-600">
          详情页每次打开会在服务端 +1 真实浏览；下方“自动加热”在每次详情打开后按概率叠加小幅增量，并受每日上限约束。
        </p>
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <p className="mb-2 text-xs text-slate-600">运营预设档位（一键填充参数，可再手动微调）</p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${inputClass} min-w-[180px]`}
              value={heatPreset}
              onChange={(e) => setHeatPreset((e.target.value as 'conservative' | 'standard' | 'aggressive') || 'standard')}
            >
              <option value="conservative">保守（增长平稳）</option>
              <option value="standard">标准（推荐）</option>
              <option value="aggressive">激进（增长更快）</option>
            </select>
            <Button type="button" variant="secondary" onClick={() => applyHeatPreset(heatPreset)}>
              应用预设
            </Button>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!mp.auto_heat?.enabled}
                onChange={(e) =>
                  setMp({
                    ...mp,
                    auto_heat: {
                      ...(mp.auto_heat || {
                        enabled: false,
                        dedupe_window_seconds: 14400,
                        heat_probability_percent: 35,
                        heat_min_increment: 1,
                        heat_max_increment: 3,
                        heat_daily_cap: 200,
                      }),
                      enabled: e.target.checked,
                    },
                  })
                }
              />
              开启：自动加热
            </label>
          </div>
          <div>
            <label className={labelClass}>去重窗口（秒，兼容旧配置）</label>
            <p className="mb-1 text-xs text-slate-500">详情每次打开均计一次浏览；该数值已不再用于去重。</p>
            <input
              type="number"
              min={300}
              max={259200}
              className={inputClass}
              value={mp.auto_heat?.dedupe_window_seconds ?? 14400}
              onChange={(e) =>
                setMp({
                  ...mp,
                  auto_heat: {
                    ...(mp.auto_heat || {}),
                    dedupe_window_seconds: Math.min(259200, Math.max(300, Number(e.target.value) || 14400)),
                  } as Miniprogram['auto_heat'],
                })
              }
            />
          </div>
          <div>
            <label className={labelClass}>加热触发概率（%）</label>
            <input
              type="number"
              min={0}
              max={100}
              className={inputClass}
              value={mp.auto_heat?.heat_probability_percent ?? 35}
              onChange={(e) =>
                setMp({
                  ...mp,
                  auto_heat: {
                    ...(mp.auto_heat || {}),
                    heat_probability_percent: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                  } as Miniprogram['auto_heat'],
                })
              }
            />
          </div>
          <div>
            <label className={labelClass}>单次最小加热</label>
            <input
              type="number"
              min={0}
              max={20}
              className={inputClass}
              value={mp.auto_heat?.heat_min_increment ?? 1}
              onChange={(e) => {
                const v = Math.min(20, Math.max(0, Number(e.target.value) || 0));
                const maxNow = Math.max(v, Number(mp.auto_heat?.heat_max_increment) || 0);
                setMp({
                  ...mp,
                  auto_heat: {
                    ...(mp.auto_heat || {}),
                    heat_min_increment: v,
                    heat_max_increment: Math.min(50, maxNow),
                  } as Miniprogram['auto_heat'],
                });
              }}
            />
          </div>
          <div>
            <label className={labelClass}>单次最大加热</label>
            <input
              type="number"
              min={0}
              max={50}
              className={inputClass}
              value={mp.auto_heat?.heat_max_increment ?? 3}
              onChange={(e) => {
                const v = Math.min(50, Math.max(0, Number(e.target.value) || 0));
                const minNow = Number(mp.auto_heat?.heat_min_increment) || 0;
                setMp({
                  ...mp,
                  auto_heat: {
                    ...(mp.auto_heat || {}),
                    heat_max_increment: Math.max(minNow, v),
                  } as Miniprogram['auto_heat'],
                });
              }}
            />
          </div>
          <div>
            <label className={labelClass}>每日加热上限（单条）</label>
            <input
              type="number"
              min={0}
              max={200000}
              className={inputClass}
              value={mp.auto_heat?.heat_daily_cap ?? 200}
              onChange={(e) =>
                setMp({
                  ...mp,
                  auto_heat: {
                    ...(mp.auto_heat || {}),
                    heat_daily_cap: Math.min(200000, Math.max(0, Number(e.target.value) || 0)),
                  } as Miniprogram['auto_heat'],
                })
              }
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存浏览量策略
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <MiniprogramFeaturesCard
        cardTitle="小程序运营扩展（首页统计 / 悬浮钮 / 广告 / 拨号）"
        features={normalizeMiniproFeatures(mp.features || defaultMiniproFeatures(), { forEdit: true })}
        sites={sites}
        miniprogramId={mp.id}
        adsManageHref={`/miniprograms/${mp.id}?view=ads`}
        onChange={(features) => setMp((prev) => (prev ? { ...prev, features } : prev))}
        onUpload={(file) => uploadMiniproImage(mp.id, file)}
        footer={
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            {saving ? '保存中…' : '保存运营扩展配置'}
          </Button>
        }
      />
      {normalizeMiniproFeatures(mp.features || defaultMiniproFeatures(), { forEdit: true }).float_actions.enabled &&
      (mp.site_ids || []).length > 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-orange-200/80 bg-orange-50/20 p-4">
          <SiteFloatActionsEditor
            sites={sites}
            siteIds={mp.site_ids || []}
            pages={mp.site_float_actions_pages || {}}
            onPatch={siteFloatPatch}
            onClear={siteFloatClear}
            onUpload={(siteId, kind, file) => void onPickFloatQrSite(siteId, kind, file)}
          />
        </div>
      ) : null}
      <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-slate-50/90 p-4">
        <h3 className="mb-1 text-sm font-semibold text-slate-900">按一级 / 二级站点覆盖运营项（可选）</h3>
        <p className="mb-4 text-xs leading-relaxed text-slate-600">
          小程序端按用户当前选中的站点生效：未单独配置的一级、二级站点分别沿用全站默认、所属一级配置（悬浮钮二维码见上方）。
        </p>
        <SiteMiniproFeaturesEditor
          sites={sites}
          siteIds={mp.site_ids || []}
          globalFeatures={normalizeMiniproFeatures(mp.features || defaultMiniproFeatures(), { forEdit: true })}
          sitePages={mp.site_minipro_features_pages || {}}
          miniprogramId={mp.id}
          onSitePagesChange={(site_minipro_features_pages) =>
            setMp((prev) => (prev ? { ...prev, site_minipro_features_pages } : prev))
          }
          onUpload={(file) => uploadMiniproImage(mp.id, file)}
        />
      </div>
      {renderSaveHint()}

      <Card title="详情页提示文案">
        <p className="mb-3 text-sm text-slate-600">用于详情页“安全提示”与“更多推荐”分区说明，建议保持简洁、可信、可执行。</p>
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <p className="mb-2 text-xs text-slate-600">预设模板（一键填充，可再手动微调）</p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${inputClass} min-w-[180px]`}
              value={detailNoticePreset}
              onChange={(e) => setDetailNoticePreset((e.target.value as 'resale' | 'hiring' | 'marriage') || 'resale')}
            >
              <option value="resale">二手交易版</option>
              <option value="hiring">招聘版</option>
              <option value="marriage">征婚版</option>
            </select>
            <Button type="button" variant="secondary" onClick={() => applyDetailNoticePreset(detailNoticePreset)}>
              应用模板
            </Button>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!mp.detail_notice?.enabled}
                onChange={(e) =>
                  setMp({
                    ...mp,
                    detail_notice: {
                      ...(mp.detail_notice || {
                        enabled: true,
                        title: '交易安全',
                        items: [
                          '仅支持当面验货、现场成交；勿向陌生人转账、勿汇款。',
                          '勿扫不明收款码；勿脱离当面场景私下付款。',
                        ],
                        more_tip: '不要转账不要汇款！线下交易，验货面付！',
                      }),
                      enabled: e.target.checked,
                    },
                  })
                }
              />
              开启：详情页安全提示
            </label>
          </div>
          <div>
            <label className={labelClass}>提示标题</label>
            <input
              className={inputClass}
              value={mp.detail_notice?.title || ''}
              onChange={(e) =>
                setMp({
                  ...mp,
                  detail_notice: {
                    ...(mp.detail_notice || { enabled: true, title: '', items: [], more_tip: '' }),
                    title: e.target.value,
                  },
                })
              }
              placeholder="如：安全提示"
            />
          </div>
          <div>
            <label className={labelClass}>更多推荐分区说明</label>
            <input
              className={inputClass}
              value={mp.detail_notice?.more_tip || ''}
              onChange={(e) =>
                setMp({
                  ...mp,
                  detail_notice: {
                    ...(mp.detail_notice || { enabled: true, title: '', items: [], more_tip: '' }),
                    more_tip: e.target.value,
                  },
                })
              }
              placeholder="如：以下内容来自同小程序展示范围，继续浏览请注意甄别信息真实性"
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>安全提示列表（每行一条）</label>
            <textarea
              className={`${inputClass} min-h-[128px]`}
              value={(mp.detail_notice?.items || []).join('\n')}
              onChange={(e) =>
                setMp({
                  ...mp,
                  detail_notice: {
                    ...(mp.detail_notice || { enabled: true, title: '', items: [], more_tip: '' }),
                    items: e.target.value
                      .split('\n')
                      .map((x) => x.trim())
                      .filter(Boolean),
                  },
                })
              }
              placeholder={'交易前请核实商品与联系人身份\n建议同城当面验货再交易'}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存详情页文案
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="详情页拨号联系提示">
        <p className="mb-3 text-sm text-slate-600">
          在售且可拨号时，显示在「拨打电话」按钮下方。可使用占位符：
          <code className="mx-1 rounded bg-slate-100 px-1">{`{miniprogram_name}`}</code>
          为上方「名称」字段、
          <code className="mx-1 rounded bg-slate-100 px-1">{`{site_name}`}</code>
          为当前信息所属站点名（随条目变化）。
        </p>
        <div className="mb-6 rounded-xl border border-amber-200/90 bg-amber-50/50 p-4">
          <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
            <input
              type="checkbox"
              checked={!!mp.phone_dial_reminder?.enabled}
              onChange={(e) =>
                setMp({
                  ...mp,
                  phone_dial_reminder: {
                    ...(mp.phone_dial_reminder ?? {
                      enabled: false,
                      title: '温馨提示',
                      body: '',
                      confirm_text: '确认拨打',
                    }),
                    enabled: e.target.checked,
                  },
                })
              }
            />
            拨号前显示「温馨提示」弹窗
          </label>
          <p className="mb-3 text-xs text-slate-600">
            开启后，用户点击「拨打电话」时先弹出提醒，点击「确认拨打」后再发起呼叫。提醒正文留空时视为关闭。
          </p>
          {mp.phone_dial_reminder?.enabled ? (
            <div className="grid gap-3 md:grid-cols-2">
              <label className={labelClass}>
                <span>弹窗标题</span>
                <input
                  className={inputClass}
                  value={mp.phone_dial_reminder?.title ?? ''}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      phone_dial_reminder: {
                        ...(mp.phone_dial_reminder ?? {
                          enabled: true,
                          title: '',
                          body: '',
                          confirm_text: '确认拨打',
                        }),
                        title: e.target.value,
                      },
                    })
                  }
                  placeholder="温馨提示"
                />
              </label>
              <label className={labelClass}>
                <span>确认按钮文案</span>
                <input
                  className={inputClass}
                  value={mp.phone_dial_reminder?.confirm_text ?? ''}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      phone_dial_reminder: {
                        ...(mp.phone_dial_reminder ?? {
                          enabled: true,
                          title: '温馨提示',
                          body: '',
                          confirm_text: '',
                        }),
                        confirm_text: e.target.value,
                      },
                    })
                  }
                  placeholder="确认拨打"
                />
              </label>
              <label className={`${labelClass} md:col-span-2`}>
                <span>提醒正文</span>
                <textarea
                  className={`${inputClass} min-h-[120px]`}
                  value={mp.phone_dial_reminder?.body ?? ''}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      phone_dial_reminder: {
                        ...(mp.phone_dial_reminder ?? {
                          enabled: true,
                          title: '温馨提示',
                          body: '',
                          confirm_text: '确认拨打',
                        }),
                        body: e.target.value,
                      },
                    })
                  }
                  placeholder="本平台仅提供信息发布…"
                />
              </label>
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                setMp({
                  ...mp,
                  phone_dial_reminder: {
                    enabled: true,
                    title: '温馨提示',
                    body: '本平台仅提供信息发布，任何收取押金，定金均有可能涉及诈骗 双方交易中,未见到货物！不要提前转账!',
                    confirm_text: '确认拨打',
                  },
                })
              }
            >
              填入默认提醒文案
            </Button>
          </div>
        </div>
        <label className={labelClass}>提示文案模板</label>
        <textarea
          className={`${inputClass} min-h-[88px]`}
          value={mp.phone_contact_hint_template ?? ''}
          onChange={(e) => setMp({ ...mp, phone_contact_hint_template: e.target.value })}
          placeholder={'留空则默认：联系时说：【{miniprogram_name}】{site_name}，看到的'}
        />
        <p className="mt-2 text-xs text-slate-500">
          若模板在替换后为空（例如未填站点名且模板仅含占位符），小程序端将不显示该行提示。
        </p>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              setMp({
                ...mp,
                phone_contact_hint_template: '联系时说：【{miniprogram_name}】{site_name}，看到的',
              })
            }
          >
            填入默认模板
          </Button>
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存拨号提示
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="拨号次数限制">
        <p className="mb-4 text-sm text-slate-600">
          开启后，用户每次点击详情页「拨打电话」将消耗当日拨号次数；次数用尽时可引导观看激励视频获得额外次数（须先在下方「运营扩展」中配置激励视频广告位）。
          关闭时与原有体验完全一致。
        </p>
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
            <input
              type="checkbox"
              checked={!!mp.dial_limit?.enabled}
              onChange={(e) =>
                setMp({
                  ...mp,
                  dial_limit: {
                    ...(mp.dial_limit ?? defaultDialLimit()),
                    enabled: e.target.checked,
                  },
                })
              }
            />
            开启：拨号限制
          </label>
          {mp.dial_limit?.enabled ? (
            <>
            <div className="grid gap-4 border-t border-slate-200/80 pt-4 md:grid-cols-2">
              <label className={labelClass}>
                <span>每日拨号限制次数</span>
                <input
                  type="number"
                  min={0}
                  max={999}
                  className={inputClass}
                  value={mp.dial_limit?.daily_limit ?? 3}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      dial_limit: {
                        ...(mp.dial_limit ?? defaultDialLimit()),
                        enabled: true,
                        daily_limit: Math.min(999, Math.max(0, Number(e.target.value) || 0)),
                      },
                    })
                  }
                />
                <span className="mt-1 block text-xs text-slate-500">
                  每位用户每天可免费拨号的次数，默认 3 次；设为 0 表示无免费额度，需观看激励视频获赠次数后才能拨打。
                </span>
              </label>
              <label className={labelClass}>
                <span>看激励广告赠拨号次数</span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  className={inputClass}
                  value={mp.dial_limit?.reward_per_ad ?? 1}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      dial_limit: {
                        ...(mp.dial_limit ?? defaultDialLimit()),
                        enabled: true,
                        reward_per_ad: Math.min(99, Math.max(1, Number(e.target.value) || 1)),
                      },
                    })
                  }
                />
                <span className="mt-1 block text-xs text-slate-500">完整观看一次激励视频赠送的次数；看多次可叠加，仅当日有效。</span>
              </label>
            </div>
            <div className="mt-4 space-y-4 border-t border-slate-200/80 pt-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={mp.dial_limit?.show_quota_on_button !== false}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      dial_limit: {
                        ...(mp.dial_limit ?? defaultDialLimit()),
                        enabled: true,
                        show_quota_on_button: e.target.checked,
                      },
                    })
                  }
                />
                按钮显示剩余拨号次数（如：拨打电话（3/10））
              </label>
              <p className="text-xs text-slate-500">
                关闭后详情页按钮仅显示「拨打电话」，不展示当日剩余/总次数。
              </p>
              <label className={labelClass}>
                <span>次数用尽弹窗标题</span>
                <input
                  type="text"
                  className={inputClass}
                  maxLength={64}
                  value={mp.dial_limit?.exhausted_modal_title ?? DIAL_LIMIT_DEFAULT_EXHAUSTED_TITLE}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      dial_limit: {
                        ...(mp.dial_limit ?? defaultDialLimit()),
                        enabled: true,
                        exhausted_modal_title: e.target.value,
                      },
                    })
                  }
                />
              </label>
              <label className={labelClass}>
                <span>次数用尽弹窗内容</span>
                <textarea
                  className={`${inputClass} min-h-[96px] resize-y`}
                  maxLength={512}
                  value={mp.dial_limit?.exhausted_modal_body ?? DIAL_LIMIT_DEFAULT_EXHAUSTED_BODY}
                  onChange={(e) =>
                    setMp({
                      ...mp,
                      dial_limit: {
                        ...(mp.dial_limit ?? defaultDialLimit()),
                        enabled: true,
                        exhausted_modal_body: e.target.value,
                      },
                    })
                  }
                />
                <span className="mt-1 block text-xs text-slate-500">
                  用户当日拨号次数用尽、引导观看激励视频时展示。可用变量{' '}
                  <code className="rounded bg-slate-100 px-1">{'{reward}'}</code> 表示「看激励广告赠拨号次数」的数值。
                </span>
              </label>
            </div>
            </>
          ) : null}
        </div>
        {mp.dial_limit?.enabled ? (
          <p className="mt-3 rounded-lg border border-amber-200/90 bg-amber-50/60 px-3 py-2 text-xs text-amber-950">
            请确认已在「基础与转发」中填写小程序 AppID / AppSecret，并在「运营扩展 → 流量主广告」中开启激励视频且填写广告位 ID，否则用户无法在次数用尽后继续拨号。
          </p>
        ) : null}
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存拨号限制
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="信息置顶 / 刷新（总配置）">
        <p className="mb-4 text-sm text-slate-600">
          置顶支持三种获取方式：激励广告、转发送置顶、联系客服。默认对所有信息类型生效；可在「信息类型管理」中为某一类型单独覆盖。与刷新组互斥。
        </p>
        <MiniprogramAdRewardSettingsCard
          cfg={mp.ad_reward ?? defaultAdReward()}
          resolveMediaUrl={resolveMediaUrl}
          onUploadServiceQr={async (file) => {
            const path = await uploadMiniproImage(mp.id, file);
            setMsg('客服置顶二维码已上传（请点击保存写入）');
            return path;
          }}
          onChange={(patch) =>
            setMp({
              ...mp,
              ad_reward: normalizeAdReward({ ...(mp.ad_reward ?? defaultAdReward()), ...patch }),
            })
          }
        />
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存置顶 / 刷新配置
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="展示站点（仅可选择您有权限的站点）">
        {mp.is_slave ? (
          <p className="mb-3 rounded-lg border border-sky-200 bg-sky-50/80 px-3 py-2 text-sm text-sky-950">
            当前为<strong>从属小程序</strong>：仅可勾选主小程序「{mp.master_name || '主小程序'}」已选范围内的站点（含其下级站点）。
          </p>
        ) : null}
        <SiteHierarchyCheckboxList
          sites={sites}
          selectedIds={mp.site_ids}
          onToggle={toggleSite}
          isItemDisabled={(siteId) => {
            if (!mp.is_slave) return false;
            const allow =
              !(mp.master_site_ids_expanded?.length ?? 0) ||
              (mp.master_site_ids_expanded ?? []).includes(siteId);
            return !allow;
          }}
          emptyHint="未加载到站点，请先在「站点管理」创建站点。"
        />
        <p className="mt-3 text-xs text-slate-500">
          「小程序列表站点名」开关在上方<strong className="text-slate-700">基础与转发</strong>卡片（与「列表加载时增加浏览量」同一区域），保存请点该卡的「保存本页配置」或本卡「保存展示站点设置」。
        </p>
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="secondary" disabled={saving} onClick={() => void saveMeta()}>
            保存展示站点设置
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="站点企业微信群引导（详情页顶部）">
        <p className="mb-3 text-sm text-slate-600">
          默认入口类型为「插件（推荐）」；请先在小程序后台添加企业微信官方插件（provider: wx4d2deeab3aed6e5a）后再发布。
          二级站点默认沿用所属一级站点的配置，可单独覆盖；勾选一级展示站点时会自动纳入其下二级站点。
        </p>
        <SiteWecomGroupsEditor
          sites={sites}
          siteIds={mp.site_ids}
          groups={mp.site_wecom_groups || {}}
          onGroupsChange={(site_wecom_groups) => setMp({ ...mp, site_wecom_groups })}
          onUploadImage={(file) => uploadMiniproImage(mp.id, file)}
          onUploadSuccess={setMsg}
          onUploadError={(m) => setErr(m)}
        />
        <div className="mt-4 flex justify-end">
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存群引导配置
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      <Card title="首页幻灯片（自上而下轮播）">
        <p className="mb-3 text-sm text-slate-600">每项一张图，可选标题与点击跳转外链（须 https）。也可粘贴已托管的图片地址。</p>
        <div className="space-y-4">
          {mp.slides.length === 0 ? <p className="text-sm text-slate-500">暂无幻灯片，点击下方添加。</p> : null}
          {mp.slides.map((sl, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className={labelClass}>图片</label>
                  <div className="mt-1 flex flex-wrap items-end gap-2">
                    <input
                      className={inputClass}
                      value={sl.image_url}
                      onChange={(e) => updateSlide(i, { image_url: e.target.value })}
                      placeholder="/uploads/... 或 https://"
                    />
                    <label className="cursor-pointer whitespace-nowrap rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50">
                      上传
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => void onPickSlide(i, e)} />
                    </label>
                  </div>
                  {sl.image_url ? (
                    <img src={resolveMediaUrl(sl.image_url)} alt="" className="mt-2 h-24 max-w-full rounded object-cover" />
                  ) : null}
                </div>
                <div>
                  <label className={labelClass}>标题（可选）</label>
                  <input className={inputClass} value={sl.title ?? ''} onChange={(e) => updateSlide(i, { title: e.target.value })} />
                  <label className={`${labelClass} mt-2`}>外链（可选）</label>
                  <input className={inputClass} value={sl.link_url ?? ''} onChange={(e) => updateSlide(i, { link_url: e.target.value })} />
                </div>
              </div>
              <Button type="button" variant="ghost" className="mt-2 text-rose-700" onClick={() => removeSlide(i)}>
                删除此项
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={addSlide}>
            添加幻灯片
          </Button>
          <Button type="button" variant="primary" disabled={saving} onClick={() => void saveMeta()}>
            保存幻灯片
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      </>
      ) : null}

      {pageView === 'ads' ? (
        <MiniprogramAdsPanel
          miniprogramId={mp.id}
          sites={sites}
          onUpload={(file) => uploadMiniproImage(mp.id, file)}
        />
      ) : null}

      {pageView === 'types' ? (
      <>
      {mp.is_slave ? (
        <div className="rounded-xl border border-amber-200/90 bg-amber-50/90 p-4 text-sm text-amber-950 shadow-sm">
          <p className="font-medium">从属小程序 · 信息类型由主小程序统一管理</p>
          <p className="mt-1 text-amber-900/90">
            类型列表为只读同步自主小程序「{mp.master_name || `#${mp.master_miniprogram_id}`}」。若需增删类型，请切换到主小程序编辑。
          </p>
          {mp.master_miniprogram_id ? (
            <div className="mt-3">
              <Link
                to={`/miniprograms/${mp.master_miniprogram_id}?view=types`}
                className="inline-flex rounded-lg bg-amber-900 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-800"
              >
                打开主小程序 · 信息类型管理
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">信息类型管理</h2>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-600">
          配置首页横向筛选标签的<strong className="font-medium text-slate-800">名称与顺序</strong>；可选为每类上传
          <strong className="font-medium text-slate-800">默认列表缩略图</strong>（当某条信息没有配图时展示）。可为单个类型开启
          <strong className="font-medium text-slate-800">发布审核</strong>并配置客服二维码，引导用户扫码加快审核。「全部」标签始终在小程序端首位，无需在此配置。
        </p>
      </div>

      <Card title="类型列表">
        <div className="mb-4 flex flex-col gap-2 border-b border-slate-100 pb-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>显示名建议不超过 8 个字；内部键由系统维护，用于数据关联。</span>
          <span className="shrink-0 font-medium text-slate-600">共 {mp.types.length} 个类型</span>
        </div>
        <div className="space-y-4">
          {mp.types.map((t, i) => (
            <div
              key={t.key || `type-new-${i}`}
              className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/5"
            >
              <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-stretch lg:gap-5">
                <div className="flex shrink-0 flex-row items-start gap-3 lg:flex-col lg:items-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">顺序</span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                      {i + 1}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-row gap-2 lg:flex-col">
                    <Button type="button" variant="secondary" size="sm" className="!px-2 !py-1 text-xs" disabled={mp.is_slave || i < 1} onClick={() => moveTypeUp(i)}>
                      上移
                    </Button>
                    <Button type="button" variant="secondary" size="sm" className="!px-2 !py-1 text-xs" disabled={mp.is_slave || i >= mp.types.length - 1} onClick={() => moveTypeDown(i)}>
                      下移
                    </Button>
                  </div>
                </div>

                <div className="min-w-0 flex-1 space-y-4">
                  <div className="max-w-md">
                    <label className={labelClass}>显示名</label>
                    <input
                      className={`${inputClass} w-full max-w-full`}
                      value={t.label}
                      disabled={!!mp.is_slave}
                      onChange={(e) => updateTypeRow(i, { label: e.target.value })}
                      placeholder="如：出租、招聘、闲置"
                      maxLength={32}
                    />
                  </div>
                  <div>
                    <span className={labelClass}>内部键</span>
                    <div className="mt-1 inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-mono text-xs text-slate-700">
                      {t.key && /^[a-z0-9_]{1,48}$/.test(t.key) ? t.key : '保存后自动生成'}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>默认列表缩略图（无配图时使用）</label>
                    <p className="mb-2 text-xs text-slate-500">推荐正方形，例如 <strong className="font-medium text-slate-700">296×296 px</strong> 或更大（与小程序列表缩略图约 148rpx 物理像素对应）。支持上传或粘贴路径。</p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <input
                          className={`${inputClass} w-full`}
                          value={t.default_cover_path || ''}
                          disabled={!!mp.is_slave}
                          onChange={(e) => updateTypeRow(i, { default_cover_path: e.target.value })}
                          placeholder="/uploads/... 留空则无默认图"
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                          <label
                            className={`inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm ${
                              mp.is_slave
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                : 'cursor-pointer border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            上传图片
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={!!mp.is_slave}
                              onChange={(e) => void onPickTypeDefaultCover(i, e)}
                            />
                          </label>
                          {(t.default_cover_path || '').trim() ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-slate-600"
                              disabled={!!mp.is_slave}
                              onClick={() => updateTypeRow(i, { default_cover_path: '' })}
                            >
                              清除默认图
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      {(t.default_cover_path || '').trim() ? (
                        <div className="shrink-0">
                          <img
                            src={resolveMediaUrl(t.default_cover_path || '')}
                            alt=""
                            className="h-[104px] w-[104px] rounded-xl border border-slate-200 object-cover sm:h-[120px] sm:w-[120px]"
                          />
                        </div>
                      ) : (
                        <div className="flex h-[104px] w-[104px] shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 sm:h-[120px] sm:w-[120px]">
                          无图
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50/90 to-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">发布审核</div>
                        <p className="mt-1 max-w-xl text-xs leading-relaxed text-slate-600">
                          开启后，该类型信息提交后<strong className="font-medium text-slate-800">必进审核流程</strong>；小程序端会提示并展示客服二维码，引导用户扫码加快审核。
                        </p>
                      </div>
                      <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                          checked={!!t.require_review}
                          disabled={!!mp.is_slave}
                          onChange={(e) => updateTypeRow(i, { require_review: e.target.checked })}
                        />
                        <span>{t.require_review ? '需审核' : '免审核'}</span>
                      </label>
                    </div>
                    {t.require_review ? (
                      <div className="mt-4 space-y-4 border-t border-amber-100/80 pt-4">
                        <div className="max-w-xl">
                          <label className={labelClass}>审核提示文案（可选）</label>
                          <input
                            className={`${inputClass} w-full`}
                            value={t.review_hint || ''}
                            disabled={!!mp.is_slave}
                            onChange={(e) => updateTypeRow(i, { review_hint: e.target.value })}
                            placeholder="如：本类信息需人工审核，添加客服微信可加快通过"
                            maxLength={200}
                          />
                          <p className="mt-1 text-xs text-slate-500">展示在发布页顶部说明与提交成功弹窗中。</p>
                        </div>
                        <div>
                          <label className={labelClass}>客服微信二维码（可选）</label>
                          <p className="mb-2 text-xs text-slate-500">
                            留空则使用「发布导流」中的全站客服二维码；建议上传正方形客服微信码。
                          </p>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                            <div className="min-w-0 flex-1">
                              <input
                                className={`${inputClass} w-full`}
                                value={t.review_service_qr_path || ''}
                                disabled={!!mp.is_slave}
                                onChange={(e) => updateTypeRow(i, { review_service_qr_path: e.target.value })}
                                placeholder="/uploads/... 留空则用全站默认"
                              />
                              <div className="mt-2 flex flex-wrap gap-2">
                                <label
                                  className={`inline-flex items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm ${
                                    mp.is_slave
                                      ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                      : 'cursor-pointer border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                                  }`}
                                >
                                  上传二维码
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    disabled={!!mp.is_slave}
                                    onChange={(e) => void onPickTypeReviewQr(i, e)}
                                  />
                                </label>
                                {(t.review_service_qr_path || '').trim() ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-slate-600"
                                    disabled={!!mp.is_slave}
                                    onClick={() => updateTypeRow(i, { review_service_qr_path: '' })}
                                  >
                                    清除二维码
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                            {(t.review_service_qr_path || '').trim() ? (
                              <div className="shrink-0">
                                <img
                                  src={resolveMediaUrl(t.review_service_qr_path || '')}
                                  alt=""
                                  className="h-[104px] w-[104px] rounded-xl border border-slate-200 object-cover sm:h-[120px] sm:w-[120px]"
                                />
                              </div>
                            ) : (
                              <div className="flex h-[104px] w-[104px] shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 sm:h-[120px] sm:w-[120px]">
                                全站默认
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/80 to-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">置顶方式</div>
                        <p className="mt-1 max-w-xl text-xs leading-relaxed text-slate-600">
                          默认跟随小程序「信息置顶 / 刷新」总配置；开启覆盖后，本类型可单独使用广告 / 转发 / 客服置顶。
                        </p>
                      </div>
                      <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          checked={!!t.pin_override}
                          disabled={!!mp.is_slave}
                          onChange={(e) => {
                            const on = e.target.checked;
                            if (!on) {
                              updateTypeRow(i, {
                                pin_override: false,
                                pin_method: undefined,
                                pin_owner_enabled: undefined,
                                pin_other_enabled: undefined,
                                pin_reward_days: undefined,
                                pin_owner_daily_limit: undefined,
                                pin_other_daily_limit: undefined,
                                share_open_required: undefined,
                                share_pin_days: undefined,
                                share_require_new_user: undefined,
                                service_qr_path: undefined,
                                service_title: undefined,
                                service_body: undefined,
                              });
                              return;
                            }
                            updateTypeRow(i, {
                              pin_override: true,
                              pin_method: t.pin_method || 'share',
                              pin_owner_enabled: true,
                            });
                          }}
                        />
                        <span>{t.pin_override ? '已覆盖总配置' : '跟随总配置'}</span>
                      </label>
                    </div>
                    {t.pin_override ? (
                      <div className="mt-4 space-y-3 border-t border-emerald-100/80 pt-4">
                        <div className="flex flex-wrap gap-2">
                          {(
                            [
                              { v: 'ad' as const, l: '激励广告' },
                              { v: 'share' as const, l: '转发送置顶' },
                              { v: 'service' as const, l: '联系客服' },
                            ] as const
                          ).map((opt) => (
                            <button
                              key={opt.v}
                              type="button"
                              disabled={!!mp.is_slave}
                              className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                                t.pin_method === opt.v
                                  ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
                                  : 'border-slate-200 bg-white text-slate-600'
                              }`}
                              onClick={() =>
                                updateTypeRow(i, {
                                  pin_method: opt.v,
                                  pin_owner_enabled: opt.v === 'ad' ? t.pin_owner_enabled ?? true : true,
                                  pin_other_enabled: opt.v === 'ad' ? t.pin_other_enabled : false,
                                })
                              }
                            >
                              {opt.l}
                            </button>
                          ))}
                        </div>
                        {t.pin_method === 'ad' ? (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!t.pin_owner_enabled}
                                disabled={!!mp.is_slave}
                                onChange={(e) => updateTypeRow(i, { pin_owner_enabled: e.target.checked })}
                              />
                              发布人看广告置顶
                            </label>
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!t.pin_other_enabled}
                                disabled={!!mp.is_slave}
                                onChange={(e) => updateTypeRow(i, { pin_other_enabled: e.target.checked })}
                              />
                              他人帮 TA 置顶
                            </label>
                            <label className="block text-sm text-slate-700">
                              <span className="mb-1 block text-xs text-slate-500">置顶天数</span>
                              <input
                                type="number"
                                min={1}
                                max={30}
                                className={`${inputClass} w-full max-w-[120px]`}
                                value={t.pin_reward_days ?? 3}
                                disabled={!!mp.is_slave}
                                onChange={(e) =>
                                  updateTypeRow(i, {
                                    pin_reward_days: Math.min(30, Math.max(1, Number(e.target.value) || 3)),
                                  })
                                }
                              />
                            </label>
                          </div>
                        ) : null}
                        {t.pin_method === 'share' ? (
                          <div className="space-y-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <label className="block text-sm text-slate-700">
                                <span className="mb-1 block text-xs text-slate-500">达标打开人数</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={999}
                                  className={`${inputClass} w-full max-w-[120px]`}
                                  value={t.share_open_required ?? 10}
                                  disabled={!!mp.is_slave}
                                  onChange={(e) =>
                                    updateTypeRow(i, {
                                      share_open_required: Math.min(999, Math.max(1, Number(e.target.value) || 10)),
                                    })
                                  }
                                />
                              </label>
                              <label className="block text-sm text-slate-700">
                                <span className="mb-1 block text-xs text-slate-500">置顶天数</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={30}
                                  className={`${inputClass} w-full max-w-[120px]`}
                                  value={t.share_pin_days ?? 3}
                                  disabled={!!mp.is_slave}
                                  onChange={(e) =>
                                    updateTypeRow(i, {
                                      share_pin_days: Math.min(30, Math.max(1, Number(e.target.value) || 3)),
                                    })
                                  }
                                />
                              </label>
                            </div>
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!t.share_require_new_user}
                                disabled={!!mp.is_slave}
                                onChange={(e) => updateTypeRow(i, { share_require_new_user: e.target.checked })}
                              />
                              仅统计平台新用户打开
                            </label>
                          </div>
                        ) : null}
                        {t.pin_method === 'service' ? (
                          <div className="space-y-3">
                            <label className="block text-sm text-slate-700">
                              <span className="mb-1 block text-xs text-slate-500">引导标题</span>
                              <input
                                className={`${inputClass} w-full max-w-md`}
                                value={t.service_title || ''}
                                disabled={!!mp.is_slave}
                                onChange={(e) => updateTypeRow(i, { service_title: e.target.value })}
                                placeholder="联系客服置顶"
                              />
                            </label>
                            <label className="block text-sm text-slate-700">
                              <span className="mb-1 block text-xs text-slate-500">引导说明</span>
                              <textarea
                                rows={2}
                                className={`${inputClass} w-full max-w-xl`}
                                value={t.service_body || ''}
                                disabled={!!mp.is_slave}
                                onChange={(e) => updateTypeRow(i, { service_body: e.target.value })}
                                placeholder="长按识别二维码添加客服…"
                              />
                            </label>
                            <div className="flex flex-wrap items-center gap-3">
                              {(t.service_qr_path || '').trim() ? (
                                <img
                                  src={resolveMediaUrl(t.service_qr_path || '')}
                                  alt=""
                                  className="h-20 w-20 rounded-lg border object-cover"
                                />
                              ) : (
                                <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed text-[11px] text-slate-400">
                                  未上传
                                </div>
                              )}
                              <label
                                className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium ${
                                  mp.is_slave
                                    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                    : 'cursor-pointer border-slate-300 bg-white text-slate-700'
                                }`}
                              >
                                上传二维码
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={!!mp.is_slave}
                                  onChange={(e) => void onPickTypePinServiceQr(i, e)}
                                />
                              </label>
                              {(t.service_qr_path || '').trim() ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={!!mp.is_slave}
                                  onClick={() => updateTypeRow(i, { service_qr_path: '' })}
                                >
                                  清除
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex shrink-0 flex-col justify-between border-t border-slate-100 pt-3 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-rose-700 hover:bg-rose-50"
                    disabled={!!mp.is_slave}
                    onClick={() => removeTypeRow(i)}
                  >
                    删除此类型
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="secondary" disabled={!!mp.is_slave} onClick={addTypeRow}>
            添加类型
          </Button>
          <Button type="button" variant="primary" disabled={saving || !!mp.is_slave} onClick={() => void saveMeta()}>
            保存类型定义
          </Button>
        </div>
        {renderSaveHint()}
      </Card>

      </>
      ) : null}

      {pageView === 'content' ? (
      <Card title="信息管理（从已选站点中、展示期内的信息里为每条指定信息类型；小程序端按类型筛选；已售信息不会进入机器人转发任务）">
        {catalogNotice ? (
          <p
            className={`mb-4 ${catalogNotice.kind === 'success' ? inlineSuccessClass : inlineErrorClass}`}
            role="status"
          >
            {catalogNotice.text}
          </p>
        ) : null}
        <div className="mb-4 rounded-xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/90 to-white p-4 shadow-sm">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-800">置顶与列表排序</div>
          <p className="mb-3 text-xs leading-relaxed text-slate-600">
            置顶信息在小程序与管理列表中均排在最前。下列排序方式影响<strong className="text-slate-800">多条置顶之间</strong>的先后，并同步用于<strong className="text-slate-800">首页横排广告栏</strong>轮播顺序；未置顶信息再按「刷新时间」（未手动刷新时等同发布时间）靠后排列。
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className={labelClass}>
              <span>置顶 / 广告栏排序方式</span>
              <select
                className={`${inputClass} h-10 min-w-[280px]`}
                value={mp.catalog_sticky_sort_mode === 'refresh_time' ? 'refresh_time' : 'remaining_days'}
                onChange={(e) =>
                  setMp({
                    ...mp,
                    catalog_sticky_sort_mode: e.target.value === 'refresh_time' ? 'refresh_time' : 'remaining_days',
                  })
                }
              >
                <option value="remaining_days">剩余天数（置顶/广告有效期剩余越多越靠前）</option>
                <option value="refresh_time">刷新/更新时间（置顶按刷新时间；广告按最近保存时间，越新越靠前）</option>
              </select>
            </label>
            <Button type="button" variant="primary" className="h-10" onClick={() => void saveCatalogStickySort()}>
              保存排序方式
            </Button>
          </div>
        </div>
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Button type="button" variant="primary" className="h-10" onClick={openCatalogAdd}>
            添加信息
          </Button>
          <p className="max-w-xl text-xs text-slate-500">在展示站点范围内新建信息；图片上传兼容本地存储与 OSS。</p>
        </div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">筛选条件</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={labelClass}>筛选站点</label>
            <select
              className={`${inputClass} h-10 min-w-[260px] max-w-[min(100%,28rem)] font-sans text-sm`}
              value={catSite}
              onChange={(e) => {
                setCatSite(Number(e.target.value));
                setCatPage(1);
              }}
            >
              <option value={0}>全部已选站点（跨层级）</option>
              {catalogSiteSelectRows.map((s) => (
                <option key={s.id} value={s.id} title={s.site_name}>
                  {s.treeLabel}
                </option>
              ))}
            </select>
            <p className="mt-1 max-w-xl text-[11px] leading-relaxed text-slate-500">
              一级站点与二级子站以缩进与「└」树线区分；含二级时一级名称后标注「（一级）」。鼠标悬停选项可查看完整站点名。
            </p>
          </div>
          <div>
            <label className={labelClass}>搜索</label>
            <input
              className={`${inputClass} h-10 min-w-[240px]`}
              value={catQ}
              onChange={(e) => setCatQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCatAppliedQ(catQ);
                  setCatAppliedType(catType);
                  setCatPage(1);
                }
              }}
              placeholder="标题、正文、编号…"
            />
          </div>
          <div>
            <label className={labelClass}>信息类型</label>
            <select
              className={`${inputClass} h-10 min-w-[160px]`}
              value={catType}
              onChange={(e) => setCatType(e.target.value)}
            >
              <option value="">全部类型</option>
              {(mp.types ?? []).map((t) => (
                <option key={t.key} value={t.key}>
                  {shortTypeLabel(t.label)}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            variant="secondary"
            className="h-10 px-4"
            onClick={() => {
              setCatAppliedQ(catQ);
              setCatAppliedType(catType);
              setCatPage(1);
            }}
          >
            搜索
          </Button>
        </div>
        </div>
        {mp.site_ids.length === 0 ? (
          <p className="text-sm text-amber-800">请先在上方勾选至少一个站点并保存。</p>
        ) : catItems.length === 0 ? (
          <p className="text-sm text-slate-600">当前条件下无展示期内的信息。</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">ID</th>
                  <th className="px-3 py-2 font-semibold">站点</th>
                  <th className="px-3 py-2 font-semibold">摘要</th>
                  <th className="px-3 py-2 font-semibold">信息类型</th>
                  <th className="px-3 py-2 font-semibold">公众号草稿</th>
                  <th className="px-3 py-2 font-semibold">发布时间</th>
                  <th className="px-3 py-2 font-semibold">刷新时间</th>
                  <th className="px-3 py-2 font-semibold">置顶状态</th>
                  <th className="min-w-[220px] px-3 py-2 font-semibold">置顶 / 刷新</th>
                  <th className="min-w-[120px] px-3 py-2 font-semibold">已售</th>
                  <th className="px-3 py-2 font-semibold">操作</th>
                </tr>
              </thead>
              <tbody>
                {catItems.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-t border-slate-100 transition-colors hover:bg-slate-50/70 ${
                      row.sold_out ? 'bg-slate-100/80' : ''
                    } ${
                      row.is_sticky
                        ? 'border-l-[4px] border-l-amber-500 bg-gradient-to-r from-amber-50/95 to-white'
                        : row.sticky_expired
                          ? 'border-l-[4px] border-l-slate-300 bg-slate-50/70'
                          : ''
                    }`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{row.id}</td>
                    <td className="px-3 py-2">
                      {catalogSiteSelectRows.length > 1 ? (
                        <select
                          className={`${inputClass} max-w-[min(100%,18rem)] py-1 font-sans text-sm sm:max-w-[20rem] ${
                            movingCatalogSiteEntryId === row.id ? 'opacity-60' : ''
                          }`}
                          value={row.site_id}
                          disabled={movingCatalogSiteEntryId === row.id}
                          title="将本条迁入所选站点（须在本小程序展示范围内：含一级及自动纳入的二级子站）"
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            if (!Number.isFinite(next) || next < 1) return;
                            void setEntrySite(row.id, next, row.site_id);
                          }}
                        >
                          {catalogSiteSelectRows.map((s) => (
                            <option key={s.id} value={s.id} title={s.site_name}>
                              {s.treeLabel}
                            </option>
                          ))}
                        </select>
                      ) : (
                        row.site_name
                      )}
                      {movingCatalogSiteEntryId === row.id ? (
                        <span className="mt-1 block text-[10px] font-medium text-sky-700">正在更换站点…</span>
                      ) : null}
                    </td>
                    <td className="max-w-md px-3 py-2 text-slate-700">
                      {(row.title_text || '').trim() || (row.body_text || '').replace(/\s+/g, ' ').slice(0, 80)}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className={inputClass + ' w-36 py-1 text-sm'}
                        value={row.type_key}
                        onChange={(e) => void setEntryType(row.id, e.target.value)}
                      >
                        {typeKeyOptionsForRow(row.type_key).map((o) => (
                          <option key={o.key || '_empty'} value={o.key}>
                            {shortTypeLabel(o.label)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="min-w-[108px] px-3 py-2 text-xs">
                      {(() => {
                        const dw = draftWechatSummary(row.draft_status, {
                          wechatDraftMediaId: row.wechat_draft_media_id,
                        });
                        return (
                          <div>
                            <span className={dw.warn ? 'font-medium text-amber-900' : 'font-medium text-emerald-800'}>
                              {dw.main}
                            </span>
                            {dw.sub ? (
                              <span className="mt-0.5 block text-[10px] leading-snug text-slate-500">{dw.sub}</span>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-600">
                      {fmtCatalogPublishedAt(row.published_at ?? row.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-600">
                      {fmtCatalogRefreshedAt(row.refreshed_at ?? undefined)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          row.is_sticky
                            ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300/80'
                            : row.sticky_expired
                              ? 'bg-slate-200 text-slate-700'
                              : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {row.pin_status_text || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <input
                            type="number"
                            min={1}
                            max={366}
                            className={`${inputClass} !h-8 !w-[4rem] !max-w-[4rem] shrink-0 !py-1 !px-2 text-center text-xs tabular-nums`}
                            value={String(catPinDaysDraft[row.id] ?? 7)}
                            onChange={(e) => {
                              const n = Math.min(366, Math.max(1, parseInt(String(e.target.value), 10) || 7));
                              setCatPinDaysDraft((d) => ({ ...d, [row.id]: n }));
                            }}
                          />
                          <span className="text-[10px] text-slate-500">天</span>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="!px-2 !py-1 text-xs"
                            disabled={pinBusyEntryId === row.id}
                            onClick={() => void setEntryPin(row.id, catPinDaysDraft[row.id] ?? 7)}
                          >
                            {pinBusyEntryId === row.id ? '…' : '置顶'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="!px-2 !py-1 text-xs text-rose-700"
                            disabled={pinBusyEntryId === row.id}
                            onClick={() => void setEntryPin(row.id, 0)}
                          >
                            取消置顶
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="!px-2 !py-1 w-fit text-xs"
                          disabled={refreshBusyEntryId === row.id}
                          onClick={() => void touchEntryRefresh(row.id)}
                        >
                          {refreshBusyEntryId === row.id ? '刷新中…' : '刷新（重置刷新时间）'}
                        </Button>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-2">
                        {row.sold_out ? (
                          <span className="inline-flex w-fit rounded-full bg-slate-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                            已售
                          </span>
                        ) : (
                          <span className="inline-flex w-fit rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200">
                            在售
                          </span>
                        )}
                        <Button
                          type="button"
                          variant={row.sold_out ? 'secondary' : 'primary'}
                          size="sm"
                          className="!px-2 !py-1 w-fit text-xs"
                          disabled={soldBusyEntryId === row.id}
                          onClick={() => void setEntrySoldOut(row.id, !row.sold_out)}
                        >
                          {soldBusyEntryId === row.id ? '…' : row.sold_out ? '恢复在售' : '标为已售'}
                        </Button>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="!px-2.5"
                            onClick={() => void openCatalogEdit(row)}
                          >
                            编辑
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            className="!px-2.5"
                            disabled={catalogDeleteBusyId === row.id}
                            onClick={() => void deleteCatalogEntry(row)}
                          >
                            {catalogDeleteBusyId === row.id ? '…' : '删除'}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          className="!px-2.5"
                          disabled={
                            copyingHashEntryId === row.id ||
                            !mp.wechat_miniprogram_appid ||
                            !mp.wechat_miniprogram_appsecret
                          }
                          onClick={() => void copyMiniProgramHashLink(row.id)}
                          title={
                            !mp.wechat_miniprogram_appid || !mp.wechat_miniprogram_appsecret
                              ? '请先配置并保存小程序 AppID + AppSecret'
                              : '复制 #小程序://... 链接'
                          }
                        >
                          {copyingHashEntryId === row.id ? '复制中…' : '小程序链接(#)'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PaginationBar
          className="mt-4"
          page={catPage}
          totalPages={Math.max(1, Math.ceil(catTotal / CAT_PER))}
          total={catTotal}
          onPrev={() => setCatPage((p) => Math.max(1, p - 1))}
          onNext={() => setCatPage((p) => Math.min(Math.max(1, Math.ceil(catTotal / CAT_PER)), p + 1))}
        />

        <Modal
          open={catalogFormOpen}
          onClose={() => !catalogFormSaving && setCatalogFormOpen(false)}
          title={catalogFormMode === 'add' ? '添加信息' : `编辑信息 #${catalogFormEntryId}`}
          wide
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={catalogFormSaving}
                onClick={() => setCatalogFormOpen(false)}
              >
                取消
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={
                  catalogFormSaving ||
                  catalogFormLoading ||
                  (catalogFormTitle.trim() === '' && catalogFormBody.trim() === '')
                }
                onClick={() => void saveCatalogForm()}
              >
                {catalogFormSaving ? '保存中…' : '保存'}
              </Button>
            </div>
          }
        >
          {catalogFormLoading ? (
            <p className="text-sm text-slate-600">加载中…</p>
          ) : (
            <div className="space-y-4">
              <label className={labelClass}>
                <span>所属站点</span>
                <select
                  className={`${inputClass} h-10`}
                  value={catalogFormSiteId}
                  disabled={catalogFormMode === 'edit' || catalogFormSaving}
                  onChange={(e) => setCatalogFormSiteId(Number(e.target.value))}
                >
                  {catalogSiteSelectRows.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.treeLabel}
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>
                <span>信息类型</span>
                <select
                  className={`${inputClass} h-10`}
                  value={catalogFormTypeKey}
                  disabled={catalogFormSaving || !!mp.is_slave}
                  onChange={(e) => setCatalogFormTypeKey(e.target.value)}
                >
                  <option value="">（未分类）</option>
                  {(mp.types ?? []).map((t) => (
                    <option key={t.key} value={t.key}>
                      {shortTypeLabel(t.label)}
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>
                <span>标题（可选）</span>
                <input
                  className={inputClass}
                  value={catalogFormTitle}
                  disabled={catalogFormSaving}
                  onChange={(e) => setCatalogFormTitle(e.target.value)}
                />
              </label>
              <label className={labelClass}>
                <span>正文</span>
                <textarea
                  className={`${inputClass} min-h-[100px]`}
                  value={catalogFormBody}
                  disabled={catalogFormSaving}
                  onChange={(e) => setCatalogFormBody(e.target.value)}
                />
              </label>
              <label className={labelClass}>
                <span>联系电话（可选）</span>
                <input
                  className={inputClass}
                  value={catalogFormPhone}
                  disabled={catalogFormSaving}
                  onChange={(e) => setCatalogFormPhone(e.target.value)}
                />
              </label>
              {catalogFormMode === 'edit' ? (
                <label className={labelClass}>
                  <span>有效期至</span>
                  <input
                    type="date"
                    className={inputClass}
                    value={catalogFormEndDate}
                    disabled={catalogFormSaving}
                    onChange={(e) => setCatalogFormEndDate(e.target.value)}
                  />
                </label>
              ) : null}
              <ManualEntryImagesField
                siteId={catalogFormSiteId}
                paths={catalogFormImages}
                onChange={setCatalogFormImages}
                disabled={catalogFormSaving}
                onError={(m) => setErr(m)}
              />
            </div>
          )}
        </Modal>
      </Card>
      ) : null}
    </div>
  );
}
